<script setup>
import { computed, ref, watch, nextTick, defineAsyncComponent, onMounted } from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import BrandIcon from 'dashboard/components/widgets/BrandIcon.vue';
import ActionLabelSelector from './ActionLabelSelector.vue';
import {
  VARIABLE_GROUPS,
  OPERATORS_BY_TYPE,
  OPERATORS_NEEDING_VALUE,
  findVariable,
} from '../conditionVariables';

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const uploadingAttachment = ref(false);

// Lista de adjuntos del step (reactive · soporta legacy singular y nuevo array).
const stepAttachments = computed(() => {
  const arr = props.step.config?.attachments;
  if (Array.isArray(arr) && arr.length) return arr;
  if (props.step.config?.attachment) return [props.step.config.attachment];
  return [];
});

// Archivos importados desde Kommo que aún no se han subido (placeholder).
// Estructura: { name, kind: 'image' | 'file', kommo_uuid }
const pendingAttachments = computed(() => {
  const arr = props.step.config?.pending_attachments;
  return Array.isArray(arr) ? arr : [];
});

function attachmentTypeLabel(ct) {
  const t = String(ct || '').toLowerCase();
  if (t.startsWith('image/')) return 'IMG';
  if (t.includes('pdf')) return 'PDF';
  if (t.includes('word') || t.includes('msword') || t.includes('officedocument.wordprocessingml')) return 'DOC';
  if (t.includes('sheet') || t.includes('excel') || t.includes('officedocument.spreadsheetml')) return 'XLS';
  if (t.startsWith('video/')) return 'VID';
  if (t.startsWith('audio/')) return 'AUD';
  return 'FILE';
}

function attachmentTypeColor(ct) {
  // Colores vibrantes + texto blanco para badges de tipo (visibles en dark/light).
  const t = String(ct || '').toLowerCase();
  if (t.startsWith('image/')) return 'bg-violet-500 text-white';
  if (t.includes('pdf')) return 'bg-rose-500 text-white';
  if (t.includes('word') || t.includes('msword') || t.includes('officedocument.wordprocessingml')) return 'bg-blue-500 text-white';
  if (t.includes('sheet') || t.includes('excel')) return 'bg-emerald-500 text-white';
  if (t.startsWith('video/')) return 'bg-amber-500 text-white';
  if (t.startsWith('audio/')) return 'bg-pink-500 text-white';
  return 'bg-slate-500 text-white';
}

function formatFileSize(bytes) {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Helper: lista normalizada de adjuntos (compat: singular legacy → array nuevo) ──
function normalizedAttachments(cfg) {
  const arr = cfg?.attachments;
  if (Array.isArray(arr) && arr.length) return arr;
  if (cfg?.attachment) return [cfg.attachment];
  return [];
}

async function uploadAttachment(ev) {
  // Soporta múltiples archivos en una sola selección.
  const files = Array.from(ev.target.files || []);
  if (!files.length) return;
  uploadingAttachment.value = true;
  try {
    const uploaded = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await window.axios.post(
        `/api/v1/accounts/${accountId.value}/deal_attachments`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      uploaded.push({
        url: data.url,
        signed_id: data.signed_id,
        filename: data.filename,
        content_type: data.content_type,
        byte_size: data.byte_size,
      });
    }
    // Append a la lista existente (no reemplaza).
    const current = normalizedAttachments(props.step.config);
    emit('update-config', {
      step: props.step,
      patch: {
        attachments: [...current, ...uploaded],
        attachment: null, // limpia el campo legacy singular para evitar duplicados
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error subiendo archivo: ' + (e.response?.data?.error || e.message));
  } finally {
    uploadingAttachment.value = false;
    ev.target.value = '';
  }
}

function removeAttachmentAt(idx) {
  const current = normalizedAttachments(props.step.config);
  const next = current.filter((_, i) => i !== idx);
  emit('update-config', { step: props.step, patch: { attachments: next, attachment: null } });
}

// Sube un archivo correspondiente a un placeholder importado de Kommo.
// Al éxito: lo mueve de pending_attachments → attachments.
const uploadingPendingIdx = ref(null);
async function uploadPendingAttachment(idx, ev) {
  const file = (ev.target.files || [])[0];
  if (!file) return;
  uploadingPendingIdx.value = idx;
  try {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/deal_attachments`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const uploaded = {
      url: data.url,
      signed_id: data.signed_id,
      filename: data.filename,
      content_type: data.content_type,
      byte_size: data.byte_size,
    };
    const currentAtts = normalizedAttachments(props.step.config);
    const currentPending = pendingAttachments.value;
    const nextPending = currentPending.filter((_, i) => i !== idx);
    emit('update-config', {
      step: props.step,
      patch: {
        attachments: [...currentAtts, uploaded],
        pending_attachments: nextPending,
        attachment: null,
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error subiendo archivo: ' + (e.response?.data?.error || e.message));
  } finally {
    uploadingPendingIdx.value = null;
    ev.target.value = '';
  }
}

function removePendingAt(idx) {
  const next = pendingAttachments.value.filter((_, i) => i !== idx);
  emit('update-config', { step: props.step, patch: { pending_attachments: next } });
}

const props = defineProps({
  step: { type: Object, required: true },
  selected: { type: Boolean, default: false },
  availableInboxes: { type: Array, default: () => [] },
  availableBots: { type: Array, default: () => [] },
  availablePipelines: { type: Array, default: () => [] },
  childrenCount: { type: Number, default: 0 },
  isRoot: { type: Boolean, default: false }, // primer step del flujo (conectado al entry pill)
  // branch_keys de los hijos ya conectados (e.g. ['value:0', 'value:2', 'else'])
  connectedBranches: { type: Array, default: () => [] },
});

// V4 · 2026-05-14 (Victor) · Cambiar etapa estilo P3 Kommo.
// Card con header (disparador "Ejecutar:") + body ("Cambiar etapa principal:" + chips).
// 3 popovers custom (trigger / pipeline / stage) que reemplazan selects nativos.
// "Desde" tiene modo Auto (default, etapa actual del lead) o Manual (gate por
// source_stage_id). "Hacia" siempre embudo + etapa destino · guarda stage_id +
// target_pipeline_id (UI helper · backend usa solo stage_id).
const cs_fromMode = computed(() => (props.step.config?.source_stage_id ? 'manual' : 'auto'));
const cs_targetPipelineId = computed(() => {
  const cfg = props.step.config || {};
  if (cfg.target_pipeline_id) return cfg.target_pipeline_id;
  const sid = parseInt(cfg.stage_id, 10);
  if (!sid) return null;
  const p = props.availablePipelines.find(pp => (pp.stages || []).some(s => s.id === sid));
  return p?.id || null;
});
const cs_sourcePipelineId = computed(() => {
  const cfg = props.step.config || {};
  if (cfg.source_pipeline_id) return cfg.source_pipeline_id;
  const sid = parseInt(cfg.source_stage_id, 10);
  if (!sid) return null;
  const p = props.availablePipelines.find(pp => (pp.stages || []).some(s => s.id === sid));
  return p?.id || null;
});
function cs_stagesOfPipeline(pipelineId) {
  if (!pipelineId) return [];
  const p = props.availablePipelines.find(pp => pp.id === parseInt(pipelineId, 10));
  return p?.stages || [];
}
function cs_pipelineById(pid) { return props.availablePipelines.find(p => p.id === parseInt(pid, 10)); }
function cs_stageById(pid, sid) {
  return (cs_pipelineById(pid)?.stages || []).find(s => s.id === parseInt(sid, 10));
}
const cs_targetPipelineName = computed(() => cs_pipelineById(cs_targetPipelineId.value)?.name || null);
const cs_targetStageName    = computed(() => cs_stageById(cs_targetPipelineId.value, props.step.config?.stage_id)?.name || null);
const cs_targetStageColor   = computed(() => cs_stageById(cs_targetPipelineId.value, props.step.config?.stage_id)?.color || '#fbbf24');
const cs_sourcePipelineName = computed(() => cs_pipelineById(cs_sourcePipelineId.value)?.name || null);
const cs_sourceStageName    = computed(() => cs_stageById(cs_sourcePipelineId.value, props.step.config?.source_stage_id)?.name || null);
const cs_sourceStageColor   = computed(() => cs_stageById(cs_sourcePipelineId.value, props.step.config?.source_stage_id)?.color || '#94a3b8');

// === Popover state (which is open) ===
// values: null | 'trigger' | 'tpipeline' | 'tstage' | 'spipeline' | 'sstage'
const cs_popOpen = ref(null);
function cs_togglePop(name) { cs_popOpen.value = cs_popOpen.value === name ? null : name; }
function cs_closePop() { cs_popOpen.value = null; }

function cs_setFromMode(mode) {
  patchConfig({ source_stage_id: null, source_pipeline_id: null }, true);
  if (mode === 'manual') cs_popOpen.value = 'spipeline';
}
function cs_setTargetPipeline(pid) {
  patchConfig({ target_pipeline_id: parseInt(pid, 10) || null, stage_id: null }, true);
  cs_popOpen.value = 'tstage';
}
function cs_setTargetStage(sid) {
  patchConfig({ stage_id: parseInt(sid, 10) || null }, true);
  cs_closePop();
}
function cs_setSourcePipeline(pid) {
  patchConfig({ source_pipeline_id: parseInt(pid, 10) || null, source_stage_id: null }, true);
  cs_popOpen.value = 'sstage';
}
function cs_setSourceStage(sid) {
  patchConfig({ source_stage_id: parseInt(sid, 10) || null }, true);
  cs_closePop();
}

// === Disparador "Ejecutar:" (UI only · fase 1) ===
// Guarda en config.execute_when un objeto { kind: string, hours?: number, minutes?: number }
// kind values: 'immediate' | 'on_created_after' | 'on_moved_after' | 'on_user_change' |
//              'on_label_added' | 'scheduled_date' | 'scheduled_daily' | 'on_message' | 'on_bot_end'
// Por ahora se persiste pero el backend no lo lee (fase 2 wirea al runner).
const CS_TRIGGER_KINDS = [
  { section: 'pipeline', label: 'Disparadores del pipeline', items: [
    { kind: 'on_created_after', label: 'Después de X cuando se crea en esta etapa', icon: '⏱', hasDelay: true },
    { kind: 'on_moved_after',   label: 'Después de X cuando es movido a esta etapa', icon: '⏱', hasDelay: true },
    { kind: 'on_user_change',   label: 'Cuando se cambia el usuario responsable',     icon: '↻' },
    { kind: 'on_label_added',   label: 'Cuando se añade una etiqueta',                icon: '🏷' },
  ]},
  { section: 'scheduled', label: 'Disparadores programados', items: [
    { kind: 'scheduled_date',  label: 'En una fecha y hora específica',  icon: '📅' },
    { kind: 'scheduled_daily', label: 'Diariamente a una hora',          icon: '🔄' },
  ]},
  { section: 'conversational', label: 'Disparadores conversacionales', items: [
    { kind: 'on_message', label: 'Cuando llega un mensaje nuevo',  icon: '💬' },
    { kind: 'on_bot_end', label: 'Cuando termina el bot anterior', icon: '⚡' },
  ]},
];
const CS_TRIGGER_KINDS_FLAT = CS_TRIGGER_KINDS.flatMap(s => s.items);
const cs_executeWhen = computed(() => props.step.config?.execute_when || null);
const cs_currentTriggerKind = computed(() => cs_executeWhen.value?.kind || 'immediate');
const cs_currentTriggerDelay = computed(() => {
  const ew = cs_executeWhen.value || {};
  return { hours: parseInt(ew.hours, 10) || 0, minutes: parseInt(ew.minutes, 10) || 0 };
});
const cs_triggerLabel = computed(() => {
  const ew = cs_executeWhen.value;
  if (!ew || ew.kind === 'immediate') return 'Cuando llega este paso (inmediato)';
  const def = CS_TRIGGER_KINDS_FLAT.find(t => t.kind === ew.kind);
  if (!def) return 'Disparador personalizado';
  if (def.hasDelay) {
    const h = parseInt(ew.hours, 10) || 0;
    const m = parseInt(ew.minutes, 10) || 0;
    const delay = h || m ? `${h}h ${m}m` : '0 min';
    return def.label.replace('X', delay);
  }
  return def.label;
});
function cs_setTriggerKind(kind) {
  const def = CS_TRIGGER_KINDS_FLAT.find(t => t.kind === kind);
  const ew = { kind };
  if (def?.hasDelay) {
    ew.hours = props.step.config?.execute_when?.hours || 0;
    ew.minutes = props.step.config?.execute_when?.minutes || 5;
  }
  patchConfig({ execute_when: ew }, true);
  if (!def?.hasDelay) cs_closePop();
}
function cs_updateTriggerDelay(field, value) {
  const ew = JSON.parse(JSON.stringify(props.step.config?.execute_when || {}));
  ew[field] = parseInt(value, 10) || 0;
  patchConfig({ execute_when: ew });
}

function isBranchConnected(branchKey) {
  return props.connectedBranches.includes(branchKey);
}
const emit = defineEmits(['select', 'add-child', 'add-branch', 'delete', 'drag-start', 'update-config', 'rename', 'duplicate']);

// ── Menú contextual del botón ⋯ (3 dots) ──
const menuOpen = ref(false);
function toggleMenu() { menuOpen.value = !menuOpen.value; }
function closeMenu() { menuOpen.value = false; }
function onRename() {
  closeMenu();
  const current = props.step.config?.label || '';
  // eslint-disable-next-line no-alert
  const newName = window.prompt('Nuevo nombre del paso:', current);
  if (newName === null) return; // cancel
  emit('rename', { step: props.step, label: newName.trim() });
}
function onDuplicate() {
  closeMenu();
  emit('duplicate', props.step);
}
function onDeleteFromMenu() {
  closeMenu();
  emit('delete', props.step);
}

// Label que se muestra en el header: custom si existe, sino el del TYPE_META
const displayLabel = computed(() => {
  const custom = props.step.config?.label;
  if (custom) return custom;
  return TYPE_META[props.step.step_type]?.label || props.step.step_type;
});

const TYPE_META = {
  message:    { label: 'Mensaje',     color: 'bg-purple-100 text-purple-700',   icon: 'M' },
  pause:      { label: 'Pausa',       color: 'text-white',                       icon: '⏸', bgStyle: 'linear-gradient(135deg,#f9a8d4,#ec4899)', shadow: '0 1px 2px rgba(236,72,153,.3)' },
  condition:  { label: 'Condición',   color: 'bg-violet-100 text-violet-700',   icon: '◇' },
  action:     { label: 'Acciones',    color: 'bg-emerald-100 text-emerald-700', icon: '✓' },
  reaction:   { label: 'Reacción',    color: 'bg-rose-100 text-rose-700',       icon: '❤' },
  comment:    { label: 'Comentario',  color: 'bg-orange-100 text-orange-700',   icon: '💭' },
  list_message:   { label: 'List Message',  color: 'bg-emerald-100 text-emerald-700', icon: '≡' },
  subscribe_meta: { label: 'Suscribirse',   color: 'bg-blue-100 text-blue-700',       icon: '○' },
  validation: { label: 'Validación',  color: 'bg-emerald-100 text-emerald-700', icon: '✓' },
  subbot:     { label: 'Iniciar Salesbot', color: 'text-white', icon: '🤖', bgStyle: 'linear-gradient(135deg,#14b8a6,#0d9488)', shadow: '0 1px 2px rgba(13,148,136,.3)' },
  code:       { label: 'Código',      color: 'bg-amber-100 text-amber-700',     icon: '{ }' },
  widget:     { label: 'Widget',      color: 'bg-cyan-100 text-cyan-700',       icon: '▦' },
  round_robin:{ label: 'Round Robin', color: 'bg-yellow-100 text-yellow-700',   icon: '↻' },
  goto:       { label: 'Ir a',        color: 'bg-indigo-100 text-indigo-700',   icon: '↪' },
};

const meta = computed(() => TYPE_META[props.step.step_type] || { label: props.step.step_type, color: 'bg-slate-100 text-slate-700', icon: '?' });

// Nombre del bot seleccionado en step de tipo 'subbot' (Iniciar Salesbot)
const selectedBotName = computed(() => {
  const id = props.step.config?.bot_id;
  if (!id) return '';
  const bot = (props.availableBots || []).find(b => b.id === id);
  return bot?.name || '';
});

// Lista normalizada de etiquetas (Array o CSV string) — para tarjeta compacta de add_label.
const labelTags = computed(() => {
  const raw = props.step.config?.labels;
  if (Array.isArray(raw)) return raw.map(s => String(s).trim()).filter(Boolean);
  if (typeof raw === 'string') return raw.split(',').map(s => s.trim()).filter(Boolean);
  return [];
});

// ── Inline picker para el card compacto de Etiqueta ───────────
const labelPickerOpen = ref(false);
const labelSearch = ref('');
const allLabels = computed(() => store.getters['labels/getLabels'] || []);
const filteredLabels = computed(() => {
  const q = labelSearch.value.trim().toLowerCase();
  if (!q) return allLabels.value;
  return allLabels.value.filter(l => (l.title || '').toLowerCase().includes(q));
});
function colorForLabel(title) {
  const found = allLabels.value.find(l => (l.title || '').toLowerCase() === title.toLowerCase());
  return found?.color || '#94a3b8';
}
function openLabelPicker() {
  store.dispatch('labels/get').catch(() => {});
  labelPickerOpen.value = true;
  labelSearch.value = '';
}

// Dispatch labels al montar para que los colores estén disponibles desde el primer render
// (antes solo se cargaban al abrir el picker, lo que dejaba los chips grises hasta ese momento).
onMounted(() => {
  if (props.step.step_type === 'action' && props.step.config?.action_type === 'add_label') {
    store.dispatch('labels/get').catch(() => {});
  }
});

// Gradiente del sidebar lateral: combina los colores reales de las etiquetas seleccionadas.
// Empty state usa un gradient sutil emerald→blue para que el card tenga identidad visual incluso vacío.
const labelSidebarGradient = computed(() => {
  const colors = labelTags.value.map(t => colorForLabel(t)).filter(Boolean);
  if (colors.length === 0) return 'linear-gradient(180deg, #10b981 0%, #3b82f6 100%)';
  if (colors.length === 1) return colors[0];
  // Múltiples colores → gradient vertical equiespaciado
  const stops = colors.map((c, i) => `${c} ${(i / (colors.length - 1) * 100).toFixed(0)}%`).join(', ');
  return `linear-gradient(180deg, ${stops})`;
});
function closeLabelPicker() {
  labelPickerOpen.value = false;
}
function isLabelSelected(title) {
  return labelTags.value.some(t => t.toLowerCase() === title.toLowerCase());
}
function toggleLabel(title) {
  const next = isLabelSelected(title)
    ? labelTags.value.filter(t => t.toLowerCase() !== title.toLowerCase())
    : [...labelTags.value, title];
  patchConfig({ labels: next }, true);
}
function removeLabelTag(title) {
  patchConfig({ labels: labelTags.value.filter(t => t.toLowerCase() !== title.toLowerCase()) }, true);
}

// Crear etiqueta nueva directamente desde el flujo (queda en el módulo
// Etiquetas porque usa el mismo store/endpoint de Labels).
const LABEL_PALETTE = [
  '#1f93ff', '#7c3aed', '#ec4899', '#f43f5e', '#f59e0b',
  '#10b981', '#14b8a6', '#6366f1', '#84cc16', '#06b6d4',
];
const creatingLabel = ref(false);
const canCreateLabel = computed(() => {
  const q = labelSearch.value.trim();
  if (!q) return false;
  return !allLabels.value.some(l => (l.title || '').toLowerCase() === q.toLowerCase());
});
async function createAndSelectLabel() {
  const title = labelSearch.value.trim();
  if (!title || creatingLabel.value) return;
  creatingLabel.value = true;
  try {
    const color = LABEL_PALETTE[Math.floor(Math.random() * LABEL_PALETTE.length)];
    await store.dispatch('labels/create', { title, description: '', color, show_on_sidebar: true });
    if (!isLabelSelected(title)) {
      patchConfig({ labels: [...labelTags.value, title] }, true);
    }
    labelSearch.value = '';
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('No se pudo crear la etiqueta: ' + (e?.response?.data?.message || e?.message || 'error'));
  } finally {
    creatingLabel.value = false;
  }
}

const previewText = computed(() => {
  const c = props.step.config || {};
  switch (props.step.step_type) {
    case 'message':
      return c.content || 'Escribe un mensaje o elige una plantilla';
    case 'pause':
      return formatPause(c);
    case 'condition':
      return c.field ? `${c.field} ${c.operator || '='} "${c.value || ''}"` : 'Sin condición';
    case 'action':
      return ACTION_LABELS[c.action_type] || c.action_type || 'Sin acción';
    case 'reaction':
      return `Emoji: ${c.emoji || '—'}`;
    case 'comment':
      return c.content ? `📝 ${c.content}` : 'Nota privada vacía';
    case 'list_message':
      return c.header || c.body || `Lista (${(c.sections || []).length} secciones)`;
    case 'validation':
      return `Esperar ${c.expect || 'any'}` + (c.timeout_minutes ? ` (${c.timeout_minutes}min)` : '');
    case 'subscribe_meta':
      return `Suscribir a "${c.list || 'broadcasts'}"`;
    case 'widget':
      return c.widget_type ? `${c.widget_type}: ${c.url || 'sin URL'}` : 'Widget sin configurar';
    case 'round_robin':
      return c.team_id ? `Team #${c.team_id}` : 'Rotación entre admins';
    default:
      return JSON.stringify(c).slice(0, 40);
  }
});

function formatPause(c) {
  const parts = [];
  if (c.hours)   parts.push(`${c.hours}h`);
  if (c.minutes) parts.push(`${c.minutes}m`);
  if (c.seconds) parts.push(`${c.seconds}s`);
  return parts.length ? '⏱ ' + parts.join(' ') : 'Sin tiempo definido';
}

const ACTION_LABELS = {
  add_label:       'Agregar etiqueta',
  remove_label:    'Quitar etiqueta',
  assign_agent:    'Asignar agente',
  assign_team:     'Asignar equipo',
  change_stage:    'Mover a etapa',
  change_priority: 'Cambiar prioridad',
  resolve:         'Resolver conversación',
  snooze:          'Posponer',
};

function onMouseDown(ev) {
  if (ev.button !== 0) return;
  if (ev.target.closest('button')) return;
  if (ev.target.closest('input')) return;
  if (ev.target.closest('textarea')) return;
  if (ev.target.closest('.no-drag')) return;
  emit('drag-start', { step: props.step, ev });
}

// ── Edición inline del mensaje (sin abrir panel lateral) ────
const msgTextareaRef = ref(null);
const showVarPicker = ref(false);
const MSG_VARIABLES = [
  '{{contact.name}}', '{{contact.phone}}', '{{contact.email}}',
  '{{deal.title}}', '{{deal.value}}', '{{deal.stage}}', '{{responsable}}',
  '{{encargada}}',
];

// Local ref desacoplado de props para evitar race condition al teclear:
// el backend responde con la versión guardada y rebinda :value al textarea
// pisando los caracteres que el usuario tecleó mientras tanto.
const localContent = ref(props.step.config?.content || '');
// Resync solo cuando cambia de step (props.step.id) — no en cada actualización del config.
watch(() => props.step.id, () => {
  localContent.value = props.step.config?.content || '';
});

let saveTimer = null;
function onContentInput(ev) {
  // Auto-resize del textarea
  const el = ev.target;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 200) + 'px';

  // Debounce save: 600ms después del último keystroke
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    emit('update-config', { step: props.step, patch: { content: localContent.value } });
  }, 600);
}

function flushContentNow() {
  // Al perder foco, guardar inmediatamente si hay un debounce pendiente
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
    emit('update-config', { step: props.step, patch: { content: localContent.value } });
  }
}

function insertVariable(v) {
  const el = msgTextareaRef.value;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const text = localContent.value || '';
  const newText = text.slice(0, start) + v + text.slice(end);
  localContent.value = newText;
  // Reaplicar el resize + save
  setTimeout(() => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, 0);
  clearTimeout(saveTimer);
  emit('update-config', { step: props.step, patch: { content: newText } });
  // Reposicionar cursor justo después de la variable
  setTimeout(() => {
    el.focus();
    el.selectionStart = el.selectionEnd = start + v.length;
  }, 0);
}

// "Variar con IA": ChatGPT reescribe el mensaje distinto cada vez al enviarlo.
const aiVariation = computed(() => !!props.step.config?.ai_variation);
function toggleAiVariation() {
  patchConfig({ ai_variation: !aiVariation.value }, true);
}

// ── Editor de Condición (Fase 1: visual con pills, schema simple) ──
// Mantiene config.field / config.operator / config.value (compatible con backend actual)
const condOpenDropdown = ref(null); // 'field' | 'operator' | 'source' | null
const condOpenRowIdx = ref(null); // qué fila abrió el dropdown (para anclar visualmente)
const condSearch = ref('');
function toggleCondDropdown(name, rowIdx = 0) {
  if (condOpenDropdown.value === name && condOpenRowIdx.value === rowIdx) {
    condOpenDropdown.value = null;
    condOpenRowIdx.value = null;
  } else {
    condOpenDropdown.value = name;
    condOpenRowIdx.value = rowIdx;
  }
  condSearch.value = '';
}
function closeCondDropdown() { condOpenDropdown.value = null; condOpenRowIdx.value = null; }

// Fuentes de valor disponibles (estilo Kommo). Solo "manual" funciona en Fase 1.
const VALUE_SOURCES = [
  { key: 'manual', label: 'Entrada manual', available: true },
  { key: 'random_long', label: 'Cadena aleatoria', available: false },
  { key: 'random_short', label: 'Cadena aleatoria corta', available: false },
  { key: 'lead.title', label: 'Lead: Nombre', available: false },
  { key: 'deal.value', label: 'Lead: Presupuesto', available: false },
  { key: 'deal.stage_name', label: 'Lead: Etapa', available: false },
  { key: 'deal.assignee_name', label: 'Lead: Responsable', available: false },
  { key: 'contact.name', label: 'Contacto: Nombre', available: false },
];
function valueSourceLabel(key) {
  return VALUE_SOURCES.find(s => s.key === (key || 'manual'))?.label || 'Entrada manual';
}
function selectValueSource(src) {
  if (!src.available) return;
  patchConfig({ value_source: src.key }, true);
  closeCondDropdown();
}

// ── Multi-valor (Fase 2A) ──
// Lee de config.values[] (nuevo). Fallback a [config.value] para bots viejos.
const condValues = computed(() => {
  const cfg = props.step.config || {};
  if (Array.isArray(cfg.values) && cfg.values.length) return cfg.values;
  if (cfg.value !== undefined && cfg.value !== null) return [cfg.value];
  return [''];
});
function updateCondValue(idx, val) {
  const next = [...condValues.value];
  next[idx] = val;
  // Mantenemos config.value sincronizado con values[0] por seguridad de rollback (el evaluador viejo lo lee)
  patchConfig({ values: next, value: next[0] || '' });
}
function addCondValue() {
  const next = [...condValues.value, ''];
  // Auto-incrementar valor: si todos son numéricos, el siguiente es N+1
  const lastNum = parseInt(next[next.length - 2], 10);
  if (!Number.isNaN(lastNum)) next[next.length - 1] = String(lastNum + 1);
  patchConfig({ values: next, value: next[0] || '' }, true);
}
function removeCondValue(idx) {
  if (condValues.value.length <= 1) return;
  const next = condValues.value.filter((_, i) => i !== idx);
  const descs = condDescriptions.value.filter((_, i) => i !== idx);
  patchConfig({ values: next, value: next[0] || '', value_descriptions: descs }, true);
}

// ── Descripciones por fila (paralelas a values) ──
// config.value_descriptions = ['Santo Domingo', 'Santiago', ...]
const condDescriptions = computed(() => {
  const arr = props.step.config?.value_descriptions;
  return Array.isArray(arr) ? arr : [];
});
function updateCondDescription(idx, desc) {
  const next = [...condDescriptions.value];
  while (next.length <= idx) next.push('');
  next[idx] = desc;
  patchConfig({ value_descriptions: next });
}
function updateElseDescription(desc) {
  patchConfig({ else_description: desc });
}

// ── Value groups: cada fila puede tener múltiples tokens (sinónimos) ──
// config.value_groups = [{ tokens: ['1','boda','casamiento'], description: 'Boda' }, ...]
// Si no existe, derivamos uno por valor (legacy compat).
const condValueGroups = computed(() => {
  const groups = props.step.config?.value_groups;
  if (Array.isArray(groups) && groups.length) {
    return groups.map(g => ({
      tokens: Array.isArray(g.tokens) ? g.tokens.filter(t => t != null && String(t).length) : [],
      description: g.description || '',
    }));
  }
  // Fallback legacy: un grupo por valor en condValues
  return condValues.value.map((v, i) => ({
    tokens: v ? [String(v)] : [],
    description: condDescriptions.value[i] || '',
  }));
});

function persistValueGroups(groups) {
  // Mantén values + value_descriptions sincronizados con groups[].tokens[0] para legacy/rollback
  patchConfig({
    value_groups: groups,
    values: groups.map(g => g.tokens[0] || ''),
    value: groups[0]?.tokens?.[0] || '',
    value_descriptions: groups.map(g => g.description || ''),
  }, true);
}

function updatePrimaryToken(idx, raw) {
  const text = String(raw || '').trim();
  const groups = condValueGroups.value.map((g, i) => {
    if (i !== idx) return g;
    const rest = g.tokens.slice(1);
    return { ...g, tokens: text ? [text, ...rest] : rest };
  });
  persistValueGroups(groups);
}

function addTokenToGroup(idx, raw) {
  const text = String(raw || '').trim();
  if (!text) return;
  // Soporta coma → varios tokens en una sola entrada
  const tokens = text.split(',').map(s => s.trim()).filter(Boolean);
  const groups = condValueGroups.value.map((g, i) => i === idx
    ? { ...g, tokens: [...g.tokens, ...tokens.filter(t => !g.tokens.some(x => x.toLowerCase() === t.toLowerCase()))] }
    : g);
  persistValueGroups(groups);
}

function removeTokenFromGroup(groupIdx, tokenIdx) {
  const groups = condValueGroups.value.map((g, i) => i === groupIdx
    ? { ...g, tokens: g.tokens.filter((_, ti) => ti !== tokenIdx) }
    : g);
  persistValueGroups(groups);
}

function updateGroupDescription(idx, desc) {
  const groups = condValueGroups.value.map((g, i) => i === idx ? { ...g, description: desc } : g);
  persistValueGroups(groups);
}

function addEmptyGroup() {
  const lastPrimary = condValueGroups.value[condValueGroups.value.length - 1]?.tokens?.[0];
  const lastNum = parseInt(lastPrimary, 10);
  const nextPrimary = !Number.isNaN(lastNum) ? String(lastNum + 1) : '';
  const groups = [...condValueGroups.value, { tokens: nextPrimary ? [nextPrimary] : [], description: '' }];
  persistValueGroups(groups);
}

function removeGroup(idx) {
  if (condValueGroups.value.length <= 1) return;
  persistValueGroups(condValueGroups.value.filter((_, i) => i !== idx));
}

// ── AND conditions (Fase 2B-1) ──
// Cada AND condition es: { field, operator, value }
const andConditions = computed(() => Array.isArray(props.step.config?.and_conditions) ? props.step.config.and_conditions : []);
function addAndCondition() {
  const next = [...andConditions.value, { field: 'message.text', operator: 'equals', value: '' }];
  patchConfig({ and_conditions: next }, true);
}
function removeAndCondition(idx) {
  const next = andConditions.value.filter((_, i) => i !== idx);
  patchConfig({ and_conditions: next }, true);
}
function updateAndCondition(idx, patch) {
  const next = andConditions.value.map((c, i) => i === idx ? { ...c, ...patch } : c);
  patchConfig({ and_conditions: next }, true);
}

// Dropdowns abiertos por AND condition (key: `and-${idx}-field` o `and-${idx}-operator`)
const andOpenDropdown = ref(null);
function toggleAndDropdown(key) {
  andOpenDropdown.value = andOpenDropdown.value === key ? null : key;
  condSearch.value = '';
}
function closeAndDropdown() { andOpenDropdown.value = null; }

// ── Reglas extra (Fase 2B-2): bloques OR adicionales ──
const extraRules = computed(() => Array.isArray(props.step.config?.extra_rules) ? props.step.config.extra_rules : []);

function emptyRule() {
  return { field: 'message.text', operator: 'equals', values: [''], value_source: 'manual', and_conditions: [] };
}

function addExtraRule() {
  const next = [...extraRules.value, emptyRule()];
  patchConfig({ extra_rules: next }, true);
}

function removeExtraRule(idx) {
  const next = extraRules.value.filter((_, i) => i !== idx);
  patchConfig({ extra_rules: next }, true);
}

function updateExtraRule(idx, patch) {
  const next = extraRules.value.map((r, i) => i === idx ? { ...r, ...patch } : r);
  patchConfig({ extra_rules: next }, true);
}

function updateExtraRuleField(ruleIdx, fieldKey) {
  const rule = extraRules.value[ruleIdx];
  const newOps = operatorsForField(fieldKey);
  const patch = { field: fieldKey };
  if (!newOps.find(o => o.key === rule.operator)) {
    patch.operator = newOps[0]?.key || 'equals';
  }
  updateExtraRule(ruleIdx, patch);
}

function updateExtraRuleValue(ruleIdx, valIdx, val) {
  const rule = extraRules.value[ruleIdx];
  const newValues = [...(rule.values || [''])];
  newValues[valIdx] = val;
  updateExtraRule(ruleIdx, { values: newValues });
}

function addExtraRuleValue(ruleIdx) {
  const rule = extraRules.value[ruleIdx];
  updateExtraRule(ruleIdx, { values: [...(rule.values || ['']), ''] });
}

function removeExtraRuleValue(ruleIdx, valIdx) {
  const rule = extraRules.value[ruleIdx];
  if ((rule.values || []).length <= 1) return;
  updateExtraRule(ruleIdx, { values: (rule.values || []).filter((_, i) => i !== valIdx) });
}

// Dropdowns activos por extra rule (key: `extra-${rIdx}-field|operator|source`)
const extraOpenDropdown = ref(null);
function toggleExtraDropdown(key) {
  extraOpenDropdown.value = extraOpenDropdown.value === key ? null : key;
  condSearch.value = '';
}
function closeExtraDropdown() { extraOpenDropdown.value = null; }
function fieldLabel(key) {
  if (!key) return 'Mensaje del cliente';
  const v = findVariable(key);
  return v?.label || key;
}
function operatorsForField(fieldKey) {
  const v = findVariable(fieldKey || 'message.text');
  return OPERATORS_BY_TYPE[v?.type || 'text'] || OPERATORS_BY_TYPE.text;
}
function operatorLabel(fieldKey, opKey) {
  const ops = operatorsForField(fieldKey);
  return ops.find(o => o.key === (opKey || 'equals'))?.label || 'Es igual a';
}
function condNeedsValue(opKey) {
  return OPERATORS_NEEDING_VALUE.includes(opKey || 'equals');
}
const filteredCondGroups = computed(() => {
  const q = condSearch.value.trim().toLowerCase();
  if (!q) return VARIABLE_GROUPS;
  return VARIABLE_GROUPS
    .map(g => ({ ...g, items: g.items.filter(i => i.label.toLowerCase().includes(q)) }))
    .filter(g => g.items.length > 0);
});
function selectCondField(key) {
  // Si el operador actual no es válido para el tipo nuevo, resetear a equals
  const newOps = operatorsForField(key);
  const current = props.step.config?.operator || 'equals';
  const patch = { field: key };
  if (!newOps.find(o => o.key === current)) {
    patch.operator = newOps[0]?.key || 'equals';
  }
  patchConfig(patch, true);
  closeCondDropdown();
}
function selectCondOperator(key) {
  patchConfig({ operator: key }, true);
  closeCondDropdown();
}

// ── Emoji picker ──
const showEmojiPicker = ref(false);
function toggleEmojiPicker() { showEmojiPicker.value = !showEmojiPicker.value; }
function closeEmojiPicker() { showEmojiPicker.value = false; }
function insertEmoji(emoji) {
  const el = msgTextareaRef.value;
  if (!el) {
    // Fallback: append al final del content si no hay textarea (no debería pasar en tipo message)
    localContent.value = (localContent.value || '') + emoji;
    emit('update-config', { step: props.step, patch: { content: localContent.value } });
    return;
  }
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const text = localContent.value || '';
  const newText = text.slice(0, start) + emoji + text.slice(end);
  localContent.value = newText;
  setTimeout(() => {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, 0);
  clearTimeout(saveTimer);
  emit('update-config', { step: props.step, patch: { content: newText } });
  setTimeout(() => {
    el.focus();
    el.selectionStart = el.selectionEnd = start + emoji.length;
  }, 0);
}

// Auto-resize inicial cuando el step cambia
watch(() => props.step.config?.content, () => {
  nextTick(() => {
    const el = msgTextareaRef.value;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    }
  });
}, { immediate: true });

// ── Patch genérico con debounce ────────────────────────────
let patchTimer = null;
const patchDraft = ref({});
function patchConfig(patch, immediate = false) {
  patchDraft.value = { ...patchDraft.value, ...patch };
  clearTimeout(patchTimer);
  if (immediate) {
    emit('update-config', { step: props.step, patch: patchDraft.value });
    patchDraft.value = {};
    return;
  }
  patchTimer = setTimeout(() => {
    emit('update-config', { step: props.step, patch: patchDraft.value });
    patchDraft.value = {};
  }, 500);
}

function onPauseInput(field, ev) {
  patchConfig({ [field]: parseInt(ev.target.value, 10) || 0 });
}

// ── Acciones: sub-tipos y campos dinámicos ─────────────────
const ACTION_TYPES = [
  { key: 'add_label',       label: 'Agregar etiqueta',       fields: [{ key: 'labels',   label: 'Etiquetas (separadas por coma)' }] },
  { key: 'remove_label',    label: 'Quitar etiqueta',        fields: [{ key: 'labels',   label: 'Etiquetas (separadas por coma)' }] },
  { key: 'assign_agent',    label: 'Asignar agente',         fields: [{ key: 'agent_id', label: 'ID del agente', type: 'number' }] },
  { key: 'assign_team',     label: 'Asignar equipo',         fields: [{ key: 'team_id',  label: 'ID del equipo', type: 'number' }] },
  { key: 'change_stage',    label: 'Mover a etapa',          fields: [{ key: 'stage_id', label: 'ID de la etapa', type: 'number' }] },
  { key: 'change_priority', label: 'Cambiar prioridad',      fields: [{ key: 'priority', label: 'Prioridad', options: ['low','medium','high','urgent'] }] },
  { key: 'resolve',         label: 'Resolver conversación',  fields: [] },
  { key: 'snooze',          label: 'Posponer',               fields: [{ key: 'hours',    label: 'Horas', type: 'number' }] },
];
function actionFieldsFor(actionType) {
  return ACTION_TYPES.find(a => a.key === actionType)?.fields || [];
}

// ── Pausa con condiciones múltiples (estilo Kommo) ─────────
const PAUSE_CONDITIONS = [
  { key: 'until_message',    label: 'Hasta recibir mensaje',         supported: true },
  { key: 'timer',            label: 'Temporizador',                  supported: true },
  { key: 'except_business',  label: 'Excepto horas laborales',       supported: false, note: 'Próximamente' },
  { key: 'until_video_open', label: 'Hasta que se abrió el video',   supported: false, note: 'Requiere integración externa' },
  { key: 'until_video_close',label: 'Hasta que se cierre el video',  supported: false, note: 'Requiere integración externa' },
];

const pauseConditions = computed(() => {
  // Backward compat: si tiene hours/min/sec sin conditions, convertir
  const cfg = props.step.config || {};
  if (Array.isArray(cfg.conditions) && cfg.conditions.length) return cfg.conditions;
  if ((cfg.hours || cfg.minutes || cfg.seconds) && props.step.step_type === 'pause') {
    return [{ type: 'timer', hours: cfg.hours || 0, minutes: cfg.minutes || 0, seconds: cfg.seconds || 0 }];
  }
  return [{ type: 'until_message' }]; // default Kommo
});

const condTypeOpenIdx = ref(null); // qué condition tiene el dropdown abierto

function condLabel(c) {
  if (c.type === 'timer') {
    return `Temporizador: ${c.hours || 0} horas ${c.minutes || 0} min ${c.seconds || 0} sec`;
  }
  return PAUSE_CONDITIONS.find(p => p.key === c.type)?.label || c.type;
}

function setCondType(idx, newType) {
  const arr = JSON.parse(JSON.stringify(pauseConditions.value));
  arr[idx] = { type: newType };
  if (newType === 'timer') Object.assign(arr[idx], { hours: 0, minutes: 0, seconds: 0 });
  patchConfig({ conditions: arr }, true);
  condTypeOpenIdx.value = null;
}

function updateCondTimer(idx, field, value) {
  const arr = JSON.parse(JSON.stringify(pauseConditions.value));
  arr[idx] = { ...arr[idx], [field]: parseInt(value, 10) || 0 };
  patchConfig({ conditions: arr });
}

function addPauseCondition() {
  const arr = JSON.parse(JSON.stringify(pauseConditions.value));
  arr.push({ type: 'until_message' });
  patchConfig({ conditions: arr }, true);
}

function removePauseCondition(idx) {
  const arr = JSON.parse(JSON.stringify(pauseConditions.value));
  arr.splice(idx, 1);
  if (!arr.length) arr.push({ type: 'until_message' }); // siempre al menos 1
  patchConfig({ conditions: arr }, true);
}

// ── Mensaje · Kommo-style: header + canales + bubble + acciones ──

// Mapea inbox.channel_type a la key de BrandIcon
function brandFor(inbox) {
  const t = (inbox.channel_type || '').split('::').pop().toLowerCase();
  // Channel::Api con nombre que contiene whatsapp → whatsapp
  if (t === 'api' && /whats|wa\b/i.test(inbox.name || '')) return 'whatsapp';
  if (t === 'whatsapp')      return 'whatsapp';
  if (t === 'instagram')     return 'instagram';
  if (t === 'facebookpage')  return 'facebook';
  if (t === 'tiktok')        return 'tiktok';
  if (t === 'telegram')      return 'telegram';
  if (t === 'twiliosms')     return 'twilio';
  if (t === 'email')         return 'email';
  if (t === 'webwidget')     return 'website';
  if (t === 'line')          return 'line';
  return 'api';
}

const showChannelDropdown = ref(false);

const selectedInboxIds = computed(() => {
  const ids = props.step.config?.inbox_ids;
  if (!Array.isArray(ids)) return null; // null = todos
  return ids;
});

const channelsLabel = computed(() => {
  if (!selectedInboxIds.value || selectedInboxIds.value.length === 0) return 'Todos';
  if (selectedInboxIds.value.length === 1) {
    const ib = props.availableInboxes.find(i => i.id === selectedInboxIds.value[0]);
    return ib ? ib.name : '1 canal';
  }
  return `${selectedInboxIds.value.length} canales`;
});

function toggleInbox(inboxId) {
  const current = Array.isArray(props.step.config?.inbox_ids) ? [...props.step.config.inbox_ids] : [];
  const idx = current.indexOf(inboxId);
  if (idx >= 0) current.splice(idx, 1);
  else current.push(inboxId);
  emit('update-config', { step: props.step, patch: { inbox_ids: current.length ? current : null } });
}

function setAllChannels() {
  emit('update-config', { step: props.step, patch: { inbox_ids: null } });
}

function isInboxSelected(inboxId) {
  if (!selectedInboxIds.value || selectedInboxIds.value.length === 0) return true;
  return selectedInboxIds.value.includes(inboxId);
}

const tryAlternative = computed({
  get: () => Boolean(props.step.config?.try_alternative),
  set: (v) => emit('update-config', { step: props.step, patch: { try_alternative: v } }),
});
</script>

<template>
  <div
    class="absolute select-none"
    :data-step-id="step.id"
    :style="{ left: step.canvas_x + 'px', top: step.canvas_y + 'px' }"
  >
    <!-- ═══════════ MESSAGE: Kommo-style elaborate card ═══════════ -->
    <div v-if="step.step_type === 'message'"
         class="bg-n-background border rounded-lg shadow-sm w-[420px]"
         :class="selected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-n-weak'"
         @mousedown="onMouseDown"
         @click.stop="emit('select', step)">

      <!-- Header bar: # | Mensaje | (a): contactos | Canales: X | ⋯ -->
      <div class="flex items-center gap-2 px-2 py-1.5 border-b border-n-weak text-[11px] cursor-move">
        <span class="font-mono text-n-slate-11 px-1.5 py-0.5 bg-n-alpha-black1 rounded text-[10px]">{{ step.id }}</span>
        <span class="font-medium text-n-slate-12 truncate" style="max-width: 140px;" :title="displayLabel">{{ displayLabel }}</span>

        <template v-if="isRoot">
        <span class="text-n-slate-11">(a):</span>
        <span class="text-n-slate-12 underline decoration-dotted underline-offset-2 truncate" style="max-width: 110px;">
          Todos los contactos
        </span>

        <span class="text-n-slate-11 ml-1">Canales:</span>

        <!-- Channels dropdown trigger -->
        <div v-on-click-outside="() => (showChannelDropdown = false)" class="relative no-drag">
          <button class="text-n-slate-12 underline decoration-dotted underline-offset-2 hover:text-blue-600"
                  @click.stop="showChannelDropdown = !showChannelDropdown">
            {{ channelsLabel }}
          </button>

          <!-- Dropdown -->
          <div v-if="showChannelDropdown"
               class="absolute z-50 left-0 top-full mt-1 w-72 bg-n-background border border-n-weak rounded-lg shadow-xl py-2"
               @mousedown.stop @click.stop>

            <div class="px-3 py-1.5 text-[11px] text-n-slate-11">Si nunca ha escrito en un canal:</div>
            <label class="px-3 py-1 flex items-center gap-2 text-xs text-n-slate-12 cursor-pointer hover:bg-n-alpha-black1">
              <span class="relative inline-block w-7 h-4 rounded-full transition" :class="tryAlternative ? 'bg-blue-500' : 'bg-n-alpha-black2'">
                <span class="absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full transition" :class="tryAlternative ? 'translate-x-3' : ''" />
              </span>
              <input v-model="tryAlternative" type="checkbox" class="sr-only" />
              Intente enviar un mensaje
            </label>

            <div class="border-t border-n-weak my-1.5"></div>

            <button class="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-n-alpha-black1 text-xs text-left"
                    @click="setAllChannels">
              <span class="w-4 h-4 inline-flex items-center justify-center" :class="(!selectedInboxIds || selectedInboxIds.length === 0) ? 'text-blue-600' : 'text-n-slate-11'">
                <span class="i-lucide-check w-3.5 h-3.5" />
              </span>
              <span class="text-n-slate-12">Ninguno (todos los canales)</span>
            </button>

            <button v-for="inbox in availableInboxes" :key="inbox.id"
                    class="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-n-alpha-black1 text-xs text-left"
                    @click="toggleInbox(inbox.id)">
              <span class="w-4 h-4 inline-flex items-center justify-center" :class="isInboxSelected(inbox.id) ? 'text-blue-600' : 'text-transparent'">
                <span class="i-lucide-check w-3.5 h-3.5" />
              </span>
              <BrandIcon :channel="brandFor(inbox)" :size="20" />
              <span class="text-n-slate-12 truncate">{{ inbox.name }}</span>
            </button>

            <div v-if="!availableInboxes.length" class="px-3 py-2 text-[11px] text-n-slate-11">
              No hay canales conectados.
            </div>
          </div>
        </div>
        </template>

        <div v-on-click-outside="closeMenu" class="relative ml-auto">
          <button class="text-n-slate-11 hover:text-n-slate-12 px-1" title="Más opciones"
                  @click.stop="toggleMenu" @mousedown.stop>⋯</button>
          <div v-if="menuOpen"
               class="absolute right-0 top-full mt-1 z-50 w-52 bg-n-background border border-n-weak rounded-lg shadow-xl py-1"
               @mousedown.stop @click.stop>
            <button class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-black1 text-left"
                    @click="onRename">
              <span class="i-lucide-pencil w-3.5 h-3.5" />
              <span>Cambiar nombre</span>
            </button>
            <button class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-black1 text-left"
                    @click="onDuplicate">
              <span class="i-lucide-copy w-3.5 h-3.5" />
              <span>Duplicar</span>
            </button>
            <button class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-600 hover:bg-red-50 text-left"
                    @click="onDeleteFromMenu">
              <span class="i-lucide-trash-2 w-3.5 h-3.5" />
              <span>Borrar</span>
            </button>
          </div>
        </div>
        <button
          type="button"
          class="w-5 h-5 rounded-full bg-blue-500 hover:bg-blue-600 text-white text-[12px] font-bold flex items-center justify-center leading-none shadow-sm transition"
          title="Agregar siguiente paso"
          @click.stop="emit('add-child', step, $event)"
        >+</button>
      </div>

      <!-- Body: blue bubble con textarea editable inline -->
      <div class="px-3 py-3 no-drag">
        <div class="rounded-lg p-2.5 flex items-start gap-2"
             style="background: linear-gradient(180deg, #4F8EF7 0%, #3B7BE8 100%);">
          <div class="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 text-base">
            🤖
          </div>
          <div class="flex-1 min-w-0">
            <textarea
              ref="msgTextareaRef"
              v-model="localContent"
              rows="1"
              placeholder="Escribe un mensaje o elige una plantilla"
              class="w-full bg-transparent border-none resize-none text-white text-[12px] leading-relaxed outline-none placeholder-white/60 placeholder:italic"
              @input="onContentInput"
              @blur="flushContentNow"
              @click.stop
              @mousedown.stop
              @wheel.stop
            />
            <div class="flex gap-1.5 mt-2 flex-wrap">
              <button class="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-white/40 text-white/80 hover:bg-white/10"
                      @click.stop="showVarPicker = !showVarPicker">
                {{ showVarPicker ? '× Cerrar variables' : '+ Variable' }}
              </button>
              <button class="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-white/40 text-white/80 hover:bg-white/10"
                      @click.stop>
                + Botón de acción
              </button>
              <button class="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-white/40 text-white/80 hover:bg-white/10"
                      @click.stop>
                + Enlace de web
              </button>
            </div>

            <!-- Variable picker (collapsible) -->
            <div v-if="showVarPicker" class="mt-2 flex gap-1 flex-wrap">
              <button v-for="v in MSG_VARIABLES" :key="v"
                      class="text-[10px] px-2 py-0.5 rounded bg-white/15 hover:bg-white/30 text-white font-mono"
                      :title="`Insertar ${v}`"
                      @click.stop="insertVariable(v)">
                {{ v }}
              </button>
            </div>

            <!-- Variar con IA: ChatGPT reescribe el mensaje distinto cada vez -->
            <div class="mt-2 pt-2 border-t border-white/20 no-drag" @mousedown.stop @click.stop>
              <label class="flex items-center gap-2 cursor-pointer select-none">
                <span class="relative inline-block w-7 h-4 rounded-full transition" :class="aiVariation ? 'bg-white' : 'bg-white/25'">
                  <span class="absolute top-0.5 left-0.5 w-3 h-3 rounded-full transition" :class="aiVariation ? 'translate-x-3 bg-blue-600' : 'bg-white'" />
                </span>
                <input :checked="aiVariation" type="checkbox" class="sr-only" @change="toggleAiVariation" />
                <span class="text-[10px] text-white font-medium">✦ Variar con IA</span>
              </label>
              <p v-if="aiVariation" class="text-[9px] text-white/70 mt-1 leading-snug">
                ChatGPT lo enviará distinto cada vez, conservando los datos (dirección, links, horarios).
              </p>
            </div>
          </div>
          <div class="flex flex-col gap-1.5 text-white/70 flex-shrink-0 items-center">
            <span class="i-lucide-mic w-3.5 h-3.5" />
            <!-- Emoji picker -->
            <div v-on-click-outside="closeEmojiPicker" class="relative no-drag" @mousedown.stop @click.stop>
              <button type="button" class="cursor-pointer hover:text-white block" title="Insertar emoji"
                      @click="toggleEmojiPicker">
                <span class="i-lucide-smile w-3.5 h-3.5 block" />
              </button>
              <div v-if="showEmojiPicker"
                   class="absolute right-full mr-2 top-0 z-50">
                <EmojiInput :on-click="insertEmoji" />
              </div>
            </div>
            <label class="cursor-pointer hover:text-white" :title="uploadingAttachment ? 'Subiendo…' : 'Adjuntar imagen, video, PDF, Word, Excel… (múltiples permitidos)'" @mousedown.stop @click.stop>
              <input type="file" class="hidden" multiple accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip" @change="uploadAttachment" />
              <span :class="uploadingAttachment ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-paperclip'" class="w-3.5 h-3.5 block" />
            </label>
            <!-- Hub único: 1 solo botón + que dispara fan-out a N ramas -->
            <button
              type="button"
              class="w-4 h-4 rounded-full ring-2 ring-white/40 text-white text-[10px] font-bold flex items-center justify-center leading-none cursor-pointer hover:bg-white/20"
              :title="childrenCount ? `Agregar otra rama (${childrenCount} ya conectadas)` : 'Agregar siguiente paso'"
              @click.stop="emit('add-child', step, $event)"
            >+</button>
          </div>
        </div>

        <!-- Lista de adjuntos · estilo C: FUERA del bubble · cards adaptan dark/light via tokens n-slate -->
        <div v-if="stepAttachments.length" class="mt-3 no-drag" @mousedown.stop @click.stop>
          <div class="text-[9px] uppercase tracking-wide font-semibold text-n-slate-11 mb-1.5 px-0.5">
            Adjuntos · {{ stepAttachments.length }} {{ stepAttachments.length === 1 ? 'archivo' : 'archivos' }}
          </div>
          <div class="space-y-1.5">
            <div v-for="(att, i) in stepAttachments" :key="`att-${i}-${att.signed_id || att.url}`"
                 class="flex items-center gap-2.5 px-2.5 py-2 rounded-md border border-n-weak bg-n-background hover:border-n-strong group/att transition-colors">
              <!-- Thumb: imagen real o badge de tipo -->
              <img v-if="(att.content_type || '').startsWith('image/')" :src="att.url"
                   class="w-9 h-9 rounded object-cover flex-shrink-0" />
              <span v-else class="w-9 h-9 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                    :class="attachmentTypeColor(att.content_type)">
                {{ attachmentTypeLabel(att.content_type) }}
              </span>
              <div class="flex-1 min-w-0">
                <div class="text-[11px] font-medium text-n-slate-12 truncate">{{ att.filename || 'archivo' }}</div>
                <div class="text-[10px] text-n-slate-11">
                  <span v-if="att.byte_size">{{ formatFileSize(att.byte_size) }} · </span>
                  <a :href="att.url" target="_blank" class="text-blue-600 hover:underline">ver</a>
                </div>
              </div>
              <button class="text-n-slate-11 hover:text-rose-500 text-base leading-none opacity-0 group-hover/att:opacity-100 transition"
                      title="Quitar archivo"
                      @click.stop="removeAttachmentAt(i)">×</button>
            </div>
          </div>
          <!-- + otro archivo · dispara el mismo input (label re-usado) -->
          <label class="inline-block text-[10px] text-blue-600 hover:underline cursor-pointer mt-1.5 px-0.5" @mousedown.stop @click.stop>
            <input type="file" class="hidden" multiple accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip" @change="uploadAttachment" />
            + otro archivo
          </label>
        </div>

        <!-- Archivos pendientes (importados desde Kommo, falta subirlos manualmente) -->
        <div v-if="pendingAttachments.length" class="mt-3 no-drag" @mousedown.stop @click.stop>
          <div class="text-[9px] uppercase tracking-wide font-semibold text-rose-500 mb-1.5 px-0.5 flex items-center gap-1">
            <span>📎</span>
            <span>Archivos pendientes de subir · {{ pendingAttachments.length }}</span>
          </div>
          <div class="space-y-1.5">
            <div v-for="(p, i) in pendingAttachments" :key="`pending-${i}-${p.kommo_uuid}`"
                 class="flex items-center gap-2.5 px-2.5 py-2 rounded-md border border-dashed border-rose-400 bg-rose-50 dark:bg-rose-900/10">
              <span class="w-9 h-9 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                    :class="p.kind === 'image' ? 'bg-violet-500 text-white' : 'bg-rose-500 text-white'">
                {{ p.kind === 'image' ? 'IMG' : 'FILE' }}
              </span>
              <div class="flex-1 min-w-0">
                <div class="text-[11px] font-medium text-n-slate-12 truncate">
                  {{ p.name || (p.kind === 'image' ? '(imagen sin nombre)' : '(archivo sin nombre)') }}
                </div>
                <div class="text-[10px] text-rose-600 dark:text-rose-400">Falta subir desde tu computadora</div>
              </div>
              <label class="text-[10px] font-semibold px-2 py-1 rounded bg-rose-500 text-white hover:bg-rose-600 cursor-pointer flex-shrink-0"
                     :class="uploadingPendingIdx === i ? 'opacity-60 pointer-events-none' : ''"
                     @mousedown.stop @click.stop>
                <input type="file" class="hidden"
                       :accept="p.kind === 'image' ? 'image/*' : '*/*'"
                       @change="(ev) => uploadPendingAttachment(i, ev)" />
                {{ uploadingPendingIdx === i ? 'Subiendo…' : 'Subir' }}
              </label>
              <button class="text-n-slate-11 hover:text-rose-500 text-base leading-none px-1"
                      title="Quitar pendiente"
                      @click.stop="removePendingAt(i)">×</button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- ═══════════ ACTION + add_label: estilo D Notion · sidebar coloreado vertical ═══════════ -->
    <div v-else-if="step.step_type === 'action' && step.config?.action_type === 'add_label'"
         class="relative bg-n-background border shadow-sm rounded-lg cursor-move"
         style="width: 340px;"
         :class="selected ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-n-weak'"
         @mousedown="onMouseDown"
         @click.stop="emit('select', step)">
      <!-- Sidebar coloreado: posicionado absolute para no requerir overflow-hidden (que clipea el picker) -->
      <div class="absolute left-0 top-0 bottom-0 rounded-l-lg pointer-events-none"
           :style="{ background: labelSidebarGradient, width: '6px' }"></div>
      <div class="ml-[6px]">
        <!-- Header -->
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-n-weak">
          <span class="text-[9px] font-mono text-n-slate-11 px-1 py-0.5 bg-n-alpha-black1 rounded shrink-0">{{ step.id }}</span>
          <span class="text-[11px] font-semibold text-n-slate-12">Etiqueta</span>
          <div v-on-click-outside="closeMenu" class="relative ml-auto shrink-0">
            <button class="text-n-slate-11 hover:text-n-slate-12 text-sm leading-none px-1" title="Más opciones"
                    @click.stop="toggleMenu" @mousedown.stop>⋯</button>
            <div v-if="menuOpen"
                 class="absolute right-0 top-full mt-1 z-50 w-48 bg-n-background border border-n-weak rounded-lg shadow-xl py-1"
                 @mousedown.stop @click.stop>
              <button class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-black1 text-left" @click="onRename">
                <span>✏️</span><span>Cambiar nombre</span>
              </button>
              <button class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-black1 text-left" @click="onDuplicate">
                <span>📋</span><span>Duplicar</span>
              </button>
              <button class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-left" @click="onDeleteFromMenu">
                <span>🗑</span><span>Eliminar</span>
              </button>
            </div>
          </div>
          <button class="w-5 h-5 rounded-full bg-blue-500 hover:bg-blue-600 text-white text-[12px] font-bold flex items-center justify-center leading-none shadow-sm transition shrink-0"
                  title="Agregar siguiente paso"
                  @click.stop="emit('add-child', step, $event)" @mousedown.stop>+</button>
        </div>
        <!-- Body: pills con colores reales -->
        <div v-on-click-outside="closeLabelPicker" class="relative px-2.5 py-2">
          <button type="button"
                  class="w-full flex items-center gap-1 flex-wrap min-h-[20px] cursor-pointer hover:bg-n-alpha-black1 rounded px-1 py-0.5 -mx-1 -my-0.5 text-left"
                  @click.stop="openLabelPicker"
                  @mousedown.stop>
            <template v-if="labelTags.length">
              <span v-for="tag in labelTags" :key="tag"
                    class="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-md text-white"
                    :style="{ background: colorForLabel(tag) }"
                    :title="tag">
                <span>{{ tag }}</span>
                <span class="w-3 h-3 rounded inline-flex items-center justify-center hover:bg-white/30 text-[10px] leading-none cursor-pointer"
                      style="background: rgba(255,255,255,.25);"
                      @click.stop="removeLabelTag(tag)" @mousedown.stop>×</span>
              </span>
              <span class="text-[10px] text-n-slate-11 italic ml-1">+ etiqueta</span>
            </template>
            <span v-else class="text-[11px] text-blue-600 hover:underline font-medium">+ Elegir o crear etiqueta</span>
          </button>
          <div v-if="labelPickerOpen"
               class="absolute top-full left-2 right-2 mt-1 bg-n-background border border-n-weak rounded-lg shadow-2xl z-50 max-h-72 overflow-hidden flex flex-col"
               @mousedown.stop @click.stop>
            <div class="p-2 border-b border-n-weak">
              <input v-model="labelSearch"
                     type="text"
                     placeholder="Buscar etiqueta…"
                     class="w-full text-xs px-2 py-1.5 rounded bg-n-alpha-black1 border border-n-weak text-n-slate-12 focus:outline-none focus:border-blue-400" />
            </div>
            <div class="overflow-y-auto flex-1">
              <button v-for="lbl in filteredLabels" :key="lbl.id || lbl.title"
                      type="button"
                      class="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-left hover:bg-n-alpha-black1"
                      :class="isLabelSelected(lbl.title) ? 'bg-emerald-500/10 text-emerald-700' : 'text-n-slate-12'"
                      @click="toggleLabel(lbl.title)">
                <span class="w-3 h-3 rounded flex-shrink-0" :style="{ background: lbl.color }" />
                <span class="flex-1 truncate">{{ lbl.title }}</span>
                <span v-if="isLabelSelected(lbl.title)" class="text-emerald-600 text-[11px]">✓</span>
              </button>
              <div v-if="filteredLabels.length === 0 && !canCreateLabel" class="px-3 py-3 text-[11px] text-n-slate-11 italic text-center">
                {{ labelSearch ? `Sin resultados para "${labelSearch}"` : 'No hay etiquetas creadas en la cuenta' }}
              </div>
            </div>
            <button v-if="canCreateLabel"
                    type="button"
                    class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left border-t border-n-weak text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-60"
                    :disabled="creatingLabel"
                    @click="createAndSelectLabel">
              <span class="text-[14px] leading-none">+</span>
              <span class="flex-1 truncate">{{ creatingLabel ? 'Creando…' : `Crear etiqueta "${labelSearch.trim()}"` }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════ Otros tipos: layout inline editable ═══════════ -->
    <div v-else
         class="border rounded-lg shadow-sm"
         :class="[
           selected ? (step.step_type === 'subbot' ? 'border-teal-500 ring-2 ring-teal-500/20' : 'border-blue-500 ring-2 ring-blue-100') : (step.step_type === 'subbot' ? 'border-teal-500/40' : 'border-n-weak'),
           step.step_type === 'condition' ? 'w-[320px]' : (step.step_type === 'pause' ? 'w-max min-w-[340px] max-w-[480px]' : (step.step_type === 'subbot' ? 'w-max min-w-[360px] max-w-[540px]' : 'w-72')),
           'bg-n-background',
         ]"
         @mousedown="onMouseDown"
         @click.stop="emit('select', step)">

      <div class="flex items-center gap-2 px-2.5 py-1.5 border-b border-n-weak cursor-move">
        <span class="text-[10px] font-mono text-n-slate-11 px-1 py-0.5 bg-n-alpha-black1 rounded">{{ step.id }}</span>
        <span class="w-5 h-5 rounded-md text-[10px] flex items-center justify-center font-semibold"
              :class="meta.color"
              :style="meta.bgStyle ? { background: meta.bgStyle, boxShadow: meta.shadow || '' } : ''">{{ meta.icon }}</span>
        <span class="text-xs font-medium text-n-slate-12 truncate" style="max-width: 140px;" :title="displayLabel">{{ displayLabel }}</span>
        <div v-on-click-outside="closeMenu" class="relative ml-auto">
          <button class="text-n-slate-11 hover:text-n-slate-12 px-1 text-xs" title="Más opciones"
                  @click.stop="toggleMenu" @mousedown.stop>⋯</button>
          <div v-if="menuOpen"
               class="absolute right-0 top-full mt-1 z-50 w-52 bg-n-background border border-n-weak rounded-lg shadow-xl py-1"
               @mousedown.stop @click.stop>
            <button class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-black1 text-left"
                    @click="onRename">
              <span class="i-lucide-pencil w-3.5 h-3.5" />
              <span>Cambiar nombre</span>
            </button>
            <button class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-n-slate-12 hover:bg-n-alpha-black1 text-left"
                    @click="onDuplicate">
              <span class="i-lucide-copy w-3.5 h-3.5" />
              <span>Duplicar</span>
            </button>
            <button class="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-red-600 hover:bg-red-50 text-left"
                    @click="onDeleteFromMenu">
              <span class="i-lucide-trash-2 w-3.5 h-3.5" />
              <span>Borrar</span>
            </button>
          </div>
        </div>
        <button
          type="button"
          class="w-5 h-5 rounded-full bg-blue-500 hover:bg-blue-600 text-white text-[12px] font-bold flex items-center justify-center leading-none shadow-sm transition"
          title="Agregar siguiente paso"
          @click.stop="emit('add-child', step, $event)"
        >+</button>
      </div>

      <div class="px-2 py-1 no-drag" @mousedown.stop @click.stop>
        <!-- PAUSA · estilo A Kommo · 1 línea por condición -->
        <template v-if="step.step_type === 'pause'">
          <div v-for="(cond, idx) in pauseConditions" :key="idx"
               class="flex items-center gap-2 px-1 py-1 rounded hover:bg-n-alpha-black1 group">
            <div class="flex-1 text-[11px] flex items-center gap-1.5 leading-[18px] whitespace-nowrap">
              <span class="text-n-slate-11">Espera</span>
              <!-- Tipo clickeable con dotted underline -->
              <div v-on-click-outside="() => { if (condTypeOpenIdx === idx) condTypeOpenIdx = null }" class="relative inline-block">
                <button type="button"
                        class="text-n-slate-12 hover:text-blue-600"
                        style="border-bottom:1px dashed #94a3b8;"
                        @click.stop="condTypeOpenIdx = condTypeOpenIdx === idx ? null : idx">
                  {{ (PAUSE_CONDITIONS.find(o => o.key === cond.type) || { label: cond.type }).label }}
                </button>
                <div v-if="condTypeOpenIdx === idx"
                     class="absolute z-30 left-0 top-full mt-1 w-64 bg-n-background border border-n-weak rounded-lg shadow-xl py-1">
                  <button v-for="opt in PAUSE_CONDITIONS" :key="opt.key"
                          class="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-n-alpha-black1 text-n-slate-12"
                          @click.stop="setCondType(idx, opt.key)">
                    <span class="w-3.5 inline-flex" :class="cond.type === opt.key ? 'text-blue-600' : 'text-transparent'">
                      <span class="i-lucide-check w-3 h-3" />
                    </span>
                    <span class="flex-1">{{ opt.label }}</span>
                    <span v-if="!opt.supported" class="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700">{{ opt.note }}</span>
                  </button>
                </div>
              </div>
              <!-- Inputs editables solo para Temporizador · grupo boxed alineado · Enter/blur commitea -->
              <template v-if="cond.type === 'timer'">
                <span class="text-n-slate-11">:</span>
                <div class="inline-flex items-stretch rounded-md overflow-hidden border border-n-weak h-[24px] align-middle">
                  <input :value="cond.hours || 0" type="number" min="0" max="999"
                         class="bg-transparent text-center font-semibold border-none outline-none focus:bg-blue-500/10 text-n-slate-12 appearance-none [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none text-[12px] leading-none"
                         style="width:30px; padding:0; height:100%;"
                         @click.stop
                         @keydown.enter.prevent="$event.target.blur()"
                         @change="updateCondTimer(idx, 'hours', $event.target.value)" />
                  <span class="text-n-slate-11 px-1.5 border-l border-n-weak inline-flex items-center text-[11px] font-medium leading-none">h</span>
                  <input :value="cond.minutes || 0" type="number" min="0" max="59"
                         class="bg-transparent text-center font-semibold border-none outline-none focus:bg-blue-500/10 text-n-slate-12 border-l border-n-weak appearance-none [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none text-[12px] leading-none"
                         style="width:30px; padding:0; height:100%;"
                         @click.stop
                         @keydown.enter.prevent="$event.target.blur()"
                         @change="updateCondTimer(idx, 'minutes', $event.target.value)" />
                  <span class="text-n-slate-11 px-1.5 border-l border-n-weak inline-flex items-center text-[11px] font-medium leading-none">m</span>
                  <input :value="cond.seconds || 0" type="number" min="0" max="59"
                         class="bg-transparent text-center font-semibold border-none outline-none focus:bg-blue-500/10 text-n-slate-12 border-l border-n-weak appearance-none [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none text-[12px] leading-none"
                         style="width:30px; padding:0; height:100%;"
                         @click.stop
                         @keydown.enter.prevent="$event.target.blur()"
                         @change="updateCondTimer(idx, 'seconds', $event.target.value)" />
                  <span class="text-n-slate-11 px-1.5 border-l border-n-weak inline-flex items-center text-[11px] font-medium leading-none">s</span>
                </div>
              </template>
            </div>
            <!-- Eliminar fila (solo si hay >1) -->
            <button v-if="pauseConditions.length > 1"
                    class="opacity-0 group-hover:opacity-100 text-n-slate-11 hover:text-rose-500 text-[12px] leading-none px-1 shrink-0"
                    title="Eliminar esta condición"
                    @click.stop="removePauseCondition(idx)">×</button>
            <!-- Conn-dot solo en la primera fila (todas comparten el mismo siguiente paso) -->
            <button v-if="idx === 0" type="button"
                    class="w-5 h-5 rounded-full flex items-center justify-center text-[10px] transition-all flex-shrink-0"
                    :class="childrenCount > 0
                      ? 'bg-emerald-500 text-white border border-emerald-600'
                      : 'border border-dashed border-n-weak text-n-slate-11 hover:border-emerald-500 hover:text-emerald-600 hover:border-solid'"
                    title="Conectar al siguiente paso"
                    @click.stop="emit('add-child', step, $event)">
              <span v-if="childrenCount > 0">→</span>
              <span v-else>○</span>
            </button>
            <span v-else class="w-5 shrink-0"></span>
          </div>

          <button class="text-[11px] text-blue-600 hover:underline px-1 py-0.5 mt-0.5"
                  @click.stop="addPauseCondition">
            + Agregar condición
          </button>
        </template>

        <!-- CONDICIÓN · estilo C ultra-minimal · 320px ancho -->
        <template v-else-if="step.step_type === 'condition'">
          <div v-on-click-outside="closeCondDropdown" class="px-1 py-0.5">

            <!-- Lista Kommo · cada fila: 2 líneas (Si campo operador: / Entrada manual [valor] sinónimos) -->
            <div v-if="condNeedsValue(step.config?.operator)">
              <div v-for="(group, idx) in condValueGroups" :key="`grp-${idx}`"
                   class="relative px-2 py-1 rounded-md transition-colors group"
                   :class="isBranchConnected(`value:${idx}`) ? 'bg-emerald-50' : 'hover:bg-slate-50 dark:hover:bg-slate-500/5'">
                <div class="flex items-start gap-2">
                  <div class="flex-1 min-w-0 text-[11px] leading-[16px]">
                    <!-- Línea 1: Si Mensaje del cliente Es igual a: -->
                    <div class="text-slate-500 dark:text-slate-400 truncate">
                      Si
                      <span class="cursor-pointer hover:text-blue-600 ml-0.5"
                            style="border-bottom:1px dashed #94a3b8;"
                            @click.stop="toggleCondDropdown('field', idx)">{{ fieldLabel(step.config?.field) }}</span>
                      <span class="cursor-pointer hover:text-blue-600 ml-1"
                            style="border-bottom:1px dashed #94a3b8;"
                            @click.stop="toggleCondDropdown('operator', idx)">{{ operatorLabel(step.config?.field, step.config?.operator) }}</span>:
                    </div>
                    <!-- Línea 2: Entrada manual [valor] sinónimos · alineados al CENTRO vertical
                         con line-height uniforme · todos comparten ~16px de altura -->
                    <div class="mt-0.5 text-slate-700 dark:text-slate-300 flex items-center gap-1 flex-wrap"
                         style="line-height:16px; min-height:16px;">
                      <span class="text-slate-400 shrink-0" style="line-height:16px;">Entrada manual</span>
                      <input :value="group.tokens[0] || ''" type="text" placeholder="?"
                             class="text-center font-semibold rounded outline-none border border-transparent focus:border-blue-400 focus:bg-white shrink-0"
                             :class="group.tokens[0] && String(group.tokens[0]).length ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'"
                             :size="Math.max(1, String(group.tokens[0] || '').length || 1)"
                             style="height:16px; line-height:16px; font-size:10px; padding:0 5px; box-sizing:border-box; width:auto; margin:0; display:inline-block; vertical-align:middle;"
                             @input="$event.target.size = Math.max(1, $event.target.value.length || 1); updatePrimaryToken(idx, $event.target.value)" />
                      <span v-for="(syn, sIdx) in group.tokens.slice(1)" :key="`syn-${idx}-${sIdx}`"
                            class="inline-flex items-center bg-slate-100 text-slate-600 border border-slate-200 group/syn shrink-0"
                            style="height:16px; line-height:14px; font-size:9.5px; font-weight:500; padding:0 6px; border-radius:8px; vertical-align:middle;">
                        <span>{{ syn }}</span>
                        <button type="button" class="opacity-0 group-hover/syn:opacity-100 hover:text-rose-500 leading-none ml-0.5"
                                title="Quitar sinónimo"
                                @click.stop="removeTokenFromGroup(idx, sIdx + 1)">×</button>
                      </span>
                      <input
                        type="text"
                        placeholder="+ sinónimo"
                        class="text-slate-400 bg-transparent border-none outline-none placeholder:text-slate-400 placeholder:italic shrink-0"
                        style="height:16px; line-height:16px; font-size:9.5px; padding:0 2px; box-sizing:border-box; width:65px; margin:0; display:inline-block; vertical-align:middle;"
                        @keydown.enter.prevent="addTokenToGroup(idx, $event.target.value); $event.target.value=''"
                        @keydown="if($event.key === ',') { $event.preventDefault(); addTokenToGroup(idx, $event.target.value); $event.target.value=''; }"
                        @blur="if($event.target.value.trim()) { addTokenToGroup(idx, $event.target.value); $event.target.value=''; }"
                      />
                    </div>
                  </div>
                  <button type="button"
                          class="w-5 h-5 rounded-full flex items-center justify-center text-[10px] transition-all flex-shrink-0 mt-1"
                          :class="isBranchConnected(`value:${idx}`)
                            ? 'bg-emerald-500 text-white border border-emerald-600'
                            : 'border border-dashed border-slate-300 text-slate-400 hover:border-emerald-500 hover:text-emerald-600 hover:border-solid'"
                          title="Click para conectar a un paso"
                          @click.stop="emit('add-branch', { step, key: `value:${idx}` }, $event)">
                    <span v-if="isBranchConnected(`value:${idx}`)">→</span>
                    <span v-else>○</span>
                  </button>
                </div>
                <button v-if="condValueGroups.length > 1" type="button"
                        class="absolute top-1.5 right-7 w-3.5 h-3.5 rounded-full bg-white border border-slate-200 text-slate-400 text-[9px] leading-none flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-rose-50 hover:text-rose-500 hover:border-rose-300"
                        title="Quitar fila"
                        @click.stop="removeGroup(idx)">×</button>

                <!-- Dropdown de campo anclado a esta fila si se abrió desde aquí -->
                <div v-if="condOpenDropdown === 'field' && condOpenRowIdx === idx"
                     class="absolute top-7 left-2 mt-1 w-72 bg-n-background border border-n-weak rounded-lg shadow-2xl z-50 max-h-72 overflow-hidden flex flex-col"
                     @mousedown.stop @click.stop>
                  <div class="p-2 border-b border-n-weak">
                    <input v-model="condSearch" placeholder="Buscar campo…"
                           class="w-full bg-n-alpha-1 border border-n-weak rounded px-2 py-1 text-xs text-n-slate-12 focus:outline-none focus:border-blue-400" />
                  </div>
                  <div class="overflow-y-auto p-2">
                    <div v-for="g in filteredCondGroups" :key="g.label" class="mb-2">
                      <div class="text-[9px] uppercase tracking-wider text-n-slate-11 mb-1 px-1">{{ g.label }}</div>
                      <button v-for="i in g.items" :key="i.key" type="button"
                              class="w-full text-left px-2 py-1 text-xs rounded text-n-slate-12 hover:bg-blue-500/10"
                              :class="(step.config?.field || 'message.text') === i.key ? 'bg-blue-500/15 text-blue-600 font-medium' : ''"
                              @click="selectCondField(i.key)">
                        {{ i.label }}
                      </button>
                    </div>
                  </div>
                </div>
                <!-- Dropdown de operador -->
                <div v-if="condOpenDropdown === 'operator' && condOpenRowIdx === idx"
                     class="absolute top-7 left-12 mt-1 w-56 bg-n-background border border-n-weak rounded-lg shadow-2xl z-50 max-h-72 overflow-y-auto"
                     @mousedown.stop @click.stop>
                  <button v-for="op in operatorsForField(step.config?.field)" :key="op.key" type="button"
                          class="w-full text-left px-3 py-1.5 text-xs text-n-slate-12 hover:bg-blue-500/10"
                          :class="(step.config?.operator || 'equals') === op.key ? 'bg-blue-500/15 text-blue-600 font-medium' : ''"
                          @click="selectCondOperator(op.key)">
                    {{ op.label }}
                  </button>
                </div>
              </div>
            </div>

            <!-- AND conditions (Fase 2B-1): todas deben pasar para evaluar primary -->
            <div v-if="andConditions.length" class="mt-2 pt-2 border-t border-dashed border-n-weak space-y-1.5">
              <div v-for="(cond, idx) in andConditions" :key="`and-${idx}`"
                   class="rounded-md bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 px-2 py-1.5 group relative">
                <div class="flex items-center flex-wrap gap-1.5 text-xs">
                  <span class="text-[9px] font-bold text-white bg-blue-500 px-1.5 py-0.5 rounded">Y</span>

                  <!-- AND field pill -->
                  <div v-on-click-outside="closeAndDropdown" class="relative">
                    <button type="button"
                            class="bg-white dark:bg-n-background border border-n-weak rounded-md px-1.5 py-0.5 text-xs text-n-slate-12 font-medium hover:border-blue-400 inline-flex items-center gap-1 max-w-[150px]"
                            :class="andOpenDropdown === `and-${idx}-field` ? 'border-blue-400 bg-blue-50 text-blue-700' : ''"
                            @click.stop="toggleAndDropdown(`and-${idx}-field`)">
                      <span class="truncate">{{ fieldLabel(cond.field) }}</span>
                      <span class="text-n-slate-11 text-[9px] flex-shrink-0">▼</span>
                    </button>
                    <div v-if="andOpenDropdown === `and-${idx}-field`"
                         class="absolute top-full left-0 mt-1 w-72 bg-n-background border border-n-weak rounded-lg shadow-2xl z-50 max-h-72 overflow-hidden flex flex-col">
                      <div class="p-2 border-b border-n-weak">
                        <input v-model="condSearch" placeholder="Buscar campo…"
                               class="w-full bg-n-alpha-1 border border-n-weak rounded px-2 py-1 text-xs text-n-slate-12 focus:outline-none focus:border-blue-400" />
                      </div>
                      <div class="overflow-y-auto p-2">
                        <div v-for="g in filteredCondGroups" :key="g.label" class="mb-2">
                          <div class="text-[9px] uppercase tracking-wider text-n-slate-11 mb-1 px-1">{{ g.label }}</div>
                          <button v-for="i in g.items" :key="i.key"
                                  type="button"
                                  class="w-full text-left px-2 py-1 text-xs rounded text-n-slate-12 hover:bg-blue-500/10"
                                  :class="cond.field === i.key ? 'bg-blue-500/15 text-blue-600 font-medium' : ''"
                                  @click="updateAndCondition(idx, { field: i.key }); closeAndDropdown()">
                            {{ i.label }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- AND operator pill -->
                  <div v-on-click-outside="closeAndDropdown" class="relative">
                    <button type="button"
                            class="bg-white dark:bg-n-background border border-n-weak rounded-md px-1.5 py-0.5 text-xs text-n-slate-12 font-medium hover:border-blue-400 inline-flex items-center gap-1"
                            :class="andOpenDropdown === `and-${idx}-operator` ? 'border-blue-400 bg-blue-50 text-blue-700' : ''"
                            @click.stop="toggleAndDropdown(`and-${idx}-operator`)">
                      {{ operatorLabel(cond.field, cond.operator) }}
                      <span class="text-n-slate-11 text-[9px]">▼</span>
                    </button>
                    <div v-if="andOpenDropdown === `and-${idx}-operator`"
                         class="absolute top-full left-0 mt-1 w-56 bg-n-background border border-n-weak rounded-lg shadow-2xl z-50 max-h-72 overflow-y-auto">
                      <button v-for="op in operatorsForField(cond.field)" :key="op.key"
                              type="button"
                              class="w-full text-left px-3 py-1.5 text-xs text-n-slate-12 hover:bg-blue-500/10"
                              :class="cond.operator === op.key ? 'bg-blue-500/15 text-blue-600 font-medium' : ''"
                              @click="updateAndCondition(idx, { operator: op.key }); closeAndDropdown()">
                        {{ op.label }}
                      </button>
                    </div>
                  </div>

                  <!-- AND value (single, no multi-value en esta fase) -->
                  <input v-if="condNeedsValue(cond.operator)"
                         :value="cond.value"
                         type="text" placeholder="valor"
                         class="flex-1 min-w-[80px] bg-white dark:bg-n-background border border-n-weak rounded px-2 py-0.5 text-xs text-n-slate-12 focus:outline-none focus:border-blue-400 font-mono"
                         @input="updateAndCondition(idx, { value: $event.target.value })" />

                  <button type="button"
                          class="ml-auto text-n-slate-11 hover:text-rose-500 text-base leading-none px-1 opacity-0 group-hover:opacity-100"
                          title="Quitar esta condición Y"
                          @click.stop="removeAndCondition(idx)">×</button>
                </div>
              </div>
            </div>

            <!-- Botón "Agregar condición (Y)" — solo visible si ya hay AND conditions o avanzado -->
            <div v-if="andConditions.length" class="mt-1 pt-1 border-t border-dashed border-n-weak flex items-center gap-3 text-[10px]">
              <button type="button"
                      class="text-blue-500 hover:text-blue-700 inline-flex items-center gap-1"
                      title="Agregar otra condición que también deba cumplirse (AND)"
                      @click.stop="addAndCondition">
                <span class="i-lucide-plus w-2.5 h-2.5" />
                + Y
              </button>
            </div>
          </div>

          <!-- Reglas extra (Fase 2B-2): cada bloque es una regla independiente OR -->
          <div v-for="(rule, rIdx) in extraRules" :key="`extra-${rIdx}`"
               class="rounded-lg border border-dashed border-n-weak bg-n-alpha-1 p-2.5 mt-2 relative group">
            <!-- Botón quitar regla -->
            <button type="button"
                    class="absolute top-1.5 right-1.5 text-n-slate-11 hover:text-rose-500 text-base leading-none px-1 opacity-0 group-hover:opacity-100"
                    title="Quitar esta regla"
                    @click.stop="removeExtraRule(rIdx)">×</button>

            <div class="flex items-center flex-wrap gap-1.5 text-xs">
              <svg class="w-3.5 h-3.5 text-n-slate-11 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
              <span class="text-n-slate-11">Si</span>

              <!-- Field pill -->
              <div v-on-click-outside="closeExtraDropdown" class="relative">
                <button type="button"
                        class="bg-n-background border border-n-weak rounded-md px-1.5 py-0.5 text-xs text-n-slate-12 font-medium hover:border-blue-400 inline-flex items-center gap-1 max-w-[150px]"
                        :class="extraOpenDropdown === `extra-${rIdx}-field` ? 'border-blue-400 bg-blue-50 text-blue-700' : ''"
                        @click.stop="toggleExtraDropdown(`extra-${rIdx}-field`)">
                  <span class="truncate">{{ fieldLabel(rule.field) }}</span>
                  <span class="text-n-slate-11 text-[9px] flex-shrink-0">▼</span>
                </button>
                <div v-if="extraOpenDropdown === `extra-${rIdx}-field`"
                     class="absolute top-full left-0 mt-1 w-72 bg-n-background border border-n-weak rounded-lg shadow-2xl z-50 max-h-72 overflow-hidden flex flex-col">
                  <div class="p-2 border-b border-n-weak">
                    <input v-model="condSearch" placeholder="Buscar campo…"
                           class="w-full bg-n-alpha-1 border border-n-weak rounded px-2 py-1 text-xs text-n-slate-12 focus:outline-none focus:border-blue-400" />
                  </div>
                  <div class="overflow-y-auto p-2">
                    <div v-for="g in filteredCondGroups" :key="g.label" class="mb-2">
                      <div class="text-[9px] uppercase tracking-wider text-n-slate-11 mb-1 px-1">{{ g.label }}</div>
                      <button v-for="i in g.items" :key="i.key" type="button"
                              class="w-full text-left px-2 py-1 text-xs rounded text-n-slate-12 hover:bg-blue-500/10"
                              :class="rule.field === i.key ? 'bg-blue-500/15 text-blue-600 font-medium' : ''"
                              @click="updateExtraRuleField(rIdx, i.key); closeExtraDropdown()">
                        {{ i.label }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Operator pill -->
              <div v-on-click-outside="closeExtraDropdown" class="relative">
                <button type="button"
                        class="bg-n-background border border-n-weak rounded-md px-1.5 py-0.5 text-xs text-n-slate-12 font-medium hover:border-blue-400 inline-flex items-center gap-1"
                        :class="extraOpenDropdown === `extra-${rIdx}-operator` ? 'border-blue-400 bg-blue-50 text-blue-700' : ''"
                        @click.stop="toggleExtraDropdown(`extra-${rIdx}-operator`)">
                  {{ operatorLabel(rule.field, rule.operator) }}
                  <span class="text-n-slate-11 text-[9px]">▼</span>
                </button>
                <div v-if="extraOpenDropdown === `extra-${rIdx}-operator`"
                     class="absolute top-full left-0 mt-1 w-56 bg-n-background border border-n-weak rounded-lg shadow-2xl z-50 max-h-72 overflow-y-auto">
                  <button v-for="op in operatorsForField(rule.field)" :key="op.key" type="button"
                          class="w-full text-left px-3 py-1.5 text-xs text-n-slate-12 hover:bg-blue-500/10"
                          :class="rule.operator === op.key ? 'bg-blue-500/15 text-blue-600 font-medium' : ''"
                          @click="updateExtraRule(rIdx, { operator: op.key }); closeExtraDropdown()">
                    {{ op.label }}
                  </button>
                </div>
              </div>

              <!-- Value source pill (estática "Entrada manual" por ahora) -->
              <div v-if="condNeedsValue(rule.operator)" class="relative">
                <button type="button" disabled
                        class="bg-n-background border border-n-weak border-dashed rounded-md px-1.5 py-0.5 text-xs text-n-slate-11 font-medium inline-flex items-center gap-1 cursor-not-allowed opacity-80">
                  Entrada manual
                  <span class="text-n-slate-11 text-[9px]">▼</span>
                </button>
              </div>
            </div>

            <!-- Lista multi-valor -->
            <div v-if="condNeedsValue(rule.operator)" class="mt-2 space-y-1">
              <div v-for="(val, vIdx) in (rule.values && rule.values.length ? rule.values : [''])" :key="`extra-${rIdx}-val-${vIdx}`"
                   class="flex items-center gap-2 px-2 py-1 rounded border border-n-weak bg-n-background group/val">
                <span class="w-1.5 h-1.5 rounded-full bg-n-slate-9 flex-shrink-0" />
                <input :value="val" type="text" :placeholder="vIdx === 0 ? 'Valor (ej. 1)' : 'Otro valor'"
                       class="flex-1 bg-transparent border-none outline-none text-xs text-n-slate-12 font-mono"
                       @input="updateExtraRuleValue(rIdx, vIdx, $event.target.value)" />
                <button type="button"
                        class="text-[10px] text-blue-500 hover:text-blue-700 opacity-0 group-hover/val:opacity-100 inline-flex items-center gap-0.5"
                        title="Conectar este valor a un paso"
                        @click.stop="emit('add-branch', { step, key: `rule:${rIdx + 1}:value:${vIdx}` }, $event)">
                  <span class="i-lucide-plus w-3 h-3" /> rama
                </button>
                <button v-if="(rule.values || []).length > 1" type="button"
                        class="text-n-slate-11 hover:text-rose-500 text-base leading-none px-1 opacity-0 group-hover/val:opacity-100"
                        title="Quitar este valor"
                        @click.stop="removeExtraRuleValue(rIdx, vIdx)">×</button>
              </div>
              <button type="button"
                      class="text-[11px] text-blue-500 hover:text-blue-700 inline-flex items-center gap-1 mt-1 ml-2"
                      @click.stop="addExtraRuleValue(rIdx)">
                <span class="i-lucide-plus w-3 h-3" />
                Agregar valor
              </button>
            </div>
          </div>

          <!-- Fila ELSE · mismo formato 2 líneas que las demás filas (compacto + uniforme) -->
          <div class="relative px-2 py-1 rounded-md transition-colors"
               :class="isBranchConnected('else') ? 'bg-slate-100 dark:bg-slate-700/30' : 'hover:bg-slate-50 dark:hover:bg-slate-500/5'">
            <div class="flex items-start gap-2">
              <div class="flex-1 min-w-0 text-[11px] leading-[16px]">
                <!-- Línea 1: igual estilo que filas value -->
                <div class="text-slate-500 dark:text-slate-400 truncate">
                  Si ningún valor coincide:
                </div>
                <!-- Línea 2: pill 'else' + descripción inline · igual altura que las pills value -->
                <div class="mt-0.5 text-slate-700 dark:text-slate-300 flex items-center gap-1 flex-wrap"
                     style="line-height:16px; min-height:16px;">
                  <span class="text-center font-semibold rounded shrink-0"
                        :class="isBranchConnected('else') ? 'bg-slate-500 text-white' : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'"
                        style="height:16px; line-height:16px; font-size:9px; padding:0 6px; letter-spacing:.04em;">
                    ELSE
                  </span>
                  <input :value="step.config?.else_description || ''" type="text"
                         placeholder="rama por defecto"
                         class="bg-transparent border-none outline-none text-slate-500 dark:text-slate-400 italic placeholder:italic placeholder:text-slate-400 dark:placeholder:text-slate-500 flex-1 min-w-[80px]"
                         style="height:16px; line-height:16px; font-size:10px; padding:0 2px;"
                         @input="updateElseDescription($event.target.value)" />
                </div>
              </div>
              <button type="button"
                      class="w-5 h-5 rounded-full flex items-center justify-center text-[10px] transition-all flex-shrink-0 mt-1"
                      :class="isBranchConnected('else')
                        ? 'bg-slate-500 text-white border border-slate-600'
                        : 'border border-dashed border-slate-300 dark:border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:border-solid'"
                      title="Conectar 'ninguna coincide'"
                      @click.stop="emit('add-branch', { step, key: 'else' }, $event)">
                <span v-if="isBranchConnected('else')">→</span>
                <span v-else>○</span>
              </button>
            </div>
          </div>

          <!-- + Agregar opción · link estilo Kommo · dashed border-top -->
          <div class="border-t border-dashed border-slate-200 mt-1.5 pt-1 text-center">
            <button type="button"
                    class="text-[10.5px] text-slate-400 hover:text-blue-600 transition-colors px-2 py-0.5"
                    @click.stop="addEmptyGroup">
              + Agregar opción
            </button>
          </div>

          <!-- Avanzado: agregar regla OR (oculto si vacío) -->
          <button v-if="extraRules.length === 0" type="button"
                  class="text-[9px] text-slate-400 hover:text-blue-500 mt-1 px-1 opacity-50 hover:opacity-100 block mx-auto"
                  title="Avanzado: agregar otra regla independiente (OR)"
                  @click.stop="addExtraRule">
            + avanzado
          </button>
        </template>

        <!-- ACCIONES -->
        <template v-else-if="step.step_type === 'action'">
          <!-- V4 · 2026-05-14 (Victor) · Si el action_type ya es change_stage, ocultar el
               dropdown "Tipo de acción" (estaba repitiendo info redundante en el card). -->
          <template v-if="step.config?.action_type !== 'change_stage'">
            <div class="text-[10px] text-n-slate-11">Tipo de acción:</div>
            <select :value="step.config?.action_type || 'add_label'"
                    class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12"
                    @change="patchConfig({ action_type: $event.target.value }, true)">
              <option v-for="a in ACTION_TYPES" :key="a.key" :value="a.key">{{ a.label }}</option>
            </select>
          </template>

          <!-- V5-P2 · Cambiar etapa · Dos secciones (Ejecutar + Acción) estilo Kommo -->
          <template v-if="step.config?.action_type === 'change_stage'">
            <div class="cs-k-card" @click.stop>
              <!-- SECCIÓN 1 · EJECUTAR (disparador) -->
              <div class="cs-k-section">
                <div class="cs-k-section-label"><span class="cs-k-section-icon">▶</span>Ejecutar</div>
                <span class="cs-k-chip cs-k-chip-trigger" @click.stop="cs_togglePop('trigger')">
                  <span class="cs-k-chip-ico">⏱</span>
                  {{ cs_triggerLabel }}
                  <span class="cs-k-chip-caret">{{ cs_popOpen === 'trigger' ? '▴' : '▾' }}</span>
                </span>
                <span class="cs-k-section-helper">click para cambiar el disparador</span>

                <!-- POPOVER · Disparador (4 categorías) -->
                <div v-if="cs_popOpen === 'trigger'" v-on-click-outside="cs_closePop" class="cs-k-pop cs-k-pop-trigger">
                  <div class="cs-k-pop-head">⚡ Cuándo ejecutar este paso
                    <span class="cs-k-pop-pill">UI primero · backend fase 2</span>
                  </div>
                  <div class="cs-k-pop-item" :class="{ selected: cs_currentTriggerKind === 'immediate' }"
                       @click.stop="cs_setTriggerKind('immediate')">
                    <span class="cs-k-pop-ico" style="background:rgba(34,197,94,0.18);color:#86efac;">→</span>
                    <div class="cs-k-pop-text">Cuando llega este paso (inmediato)
                      <span class="cs-k-pop-meta">default · sin delay</span>
                    </div>
                    <span v-if="cs_currentTriggerKind === 'immediate'" class="cs-k-pop-check">✓</span>
                  </div>
                  <template v-for="sec in CS_TRIGGER_KINDS" :key="sec.section">
                    <div class="cs-k-pop-divider"></div>
                    <div class="cs-k-pop-section">{{ sec.label }}</div>
                    <template v-for="t in sec.items" :key="t.kind">
                      <div class="cs-k-pop-item" :class="{ selected: cs_currentTriggerKind === t.kind }"
                           @click.stop="cs_setTriggerKind(t.kind)">
                        <span class="cs-k-pop-ico">{{ t.icon }}</span>
                        <div class="cs-k-pop-text">{{ t.label.replace('X', t.hasDelay && cs_currentTriggerKind === t.kind ? `${cs_currentTriggerDelay.hours}h ${cs_currentTriggerDelay.minutes}m` : 'X tiempo') }}</div>
                        <span v-if="cs_currentTriggerKind === t.kind" class="cs-k-pop-check">✓</span>
                      </div>
                      <div v-if="t.hasDelay && cs_currentTriggerKind === t.kind" class="cs-k-pop-delay">
                        <span class="cs-k-pop-delay-label">Delay:</span>
                        <input type="number" min="0" :value="cs_currentTriggerDelay.hours"
                               @input.stop="cs_updateTriggerDelay('hours', $event.target.value)" @click.stop>
                        <span>horas</span>
                        <input type="number" min="0" max="59" :value="cs_currentTriggerDelay.minutes"
                               @input.stop="cs_updateTriggerDelay('minutes', $event.target.value)" @click.stop>
                        <span>min</span>
                      </div>
                    </template>
                  </template>
                </div>
              </div>

              <!-- SECCIÓN 2 · ACCIÓN (oración natural) -->
              <div class="cs-k-section">
                <div class="cs-k-section-label"><span class="cs-k-section-icon">→</span>Acción</div>
                <span class="cs-k-word">Mueve el lead desde</span>
                <div class="cs-k-toggle">
                  <button type="button" :class="['cs-k-toggle-btn', { active: cs_fromMode === 'auto' }]"
                          @click.stop="cs_setFromMode('auto')">⚡ Auto</button>
                  <button type="button" :class="['cs-k-toggle-btn', { active: cs_fromMode === 'manual' }]"
                          @click.stop="cs_setFromMode('manual')">✋ Manual</button>
                </div>

                <!-- Manual mode: chips embudo + etapa fuente -->
                <template v-if="cs_fromMode === 'manual'">
                  <span class="cs-k-chip cs-k-chip-from" :class="{ placeholder: !cs_sourcePipelineName }"
                        @click.stop="cs_togglePop('spipeline')">
                    <span v-if="cs_sourcePipelineName" class="cs-k-chip-dot" style="background:#60a5fa;"></span>
                    {{ cs_sourcePipelineName || 'elige embudo' }}
                    <span class="cs-k-chip-caret">{{ cs_popOpen === 'spipeline' ? '▴' : '▾' }}</span>
                  </span>
                  <span class="cs-k-chip cs-k-chip-from" :class="{ placeholder: !cs_sourceStageName, disabled: !cs_sourcePipelineId }"
                        @click.stop="cs_sourcePipelineId && cs_togglePop('sstage')">
                    <span v-if="cs_sourceStageName" class="cs-k-chip-dot" :style="{ background: cs_sourceStageColor }"></span>
                    {{ cs_sourceStageName || 'elige etapa' }}
                    <span class="cs-k-chip-caret">{{ cs_popOpen === 'sstage' ? '▴' : '▾' }}</span>
                  </span>
                </template>

                <span class="cs-k-word">hacia</span>

                <!-- Hacia: chips embudo + etapa destino -->
                <span class="cs-k-chip cs-k-chip-to" :class="{ placeholder: !cs_targetPipelineName }"
                      @click.stop="cs_togglePop('tpipeline')">
                  <span v-if="cs_targetPipelineName" class="cs-k-chip-dot" style="background:#60a5fa;"></span>
                  {{ cs_targetPipelineName || 'elige embudo' }}
                  <span class="cs-k-chip-caret">{{ cs_popOpen === 'tpipeline' ? '▴' : '▾' }}</span>
                </span>
                <span class="cs-k-word">en</span>
                <span class="cs-k-chip cs-k-chip-to" :class="{ placeholder: !cs_targetStageName, disabled: !cs_targetPipelineId }"
                      @click.stop="cs_targetPipelineId && cs_togglePop('tstage')">
                  <span v-if="cs_targetStageName" class="cs-k-chip-dot" :style="{ background: cs_targetStageColor }"></span>
                  {{ cs_targetStageName || 'elige etapa' }}
                  <span class="cs-k-chip-caret">{{ cs_popOpen === 'tstage' ? '▴' : '▾' }}</span>
                </span>

                <!-- POPOVERS embudo · etapa (4 instancias: source pipeline/stage + target pipeline/stage) -->
                <div v-if="cs_popOpen === 'tpipeline'" v-on-click-outside="cs_closePop" class="cs-k-pop cs-k-pop-pipe">
                  <div class="cs-k-pop-head">Elige embudo destino <span class="cs-k-pop-pill">{{ availablePipelines.length }} embudos</span></div>
                  <div v-for="p in availablePipelines" :key="p.id" class="cs-k-pop-item"
                       :class="{ selected: cs_targetPipelineId === p.id }"
                       @click.stop="cs_setTargetPipeline(p.id)">
                    <span class="cs-k-pop-ico" style="background:rgba(96,165,250,0.18);color:#93c5fd;">⊟</span>
                    <div class="cs-k-pop-text">{{ p.name }}
                      <span class="cs-k-pop-meta">{{ (p.stages || []).length }} etapas</span>
                    </div>
                    <span v-if="cs_targetPipelineId === p.id" class="cs-k-pop-check">✓</span>
                  </div>
                </div>

                <div v-if="cs_popOpen === 'tstage'" v-on-click-outside="cs_closePop" class="cs-k-pop cs-k-pop-stage">
                  <div class="cs-k-pop-head">Etapa destino · "{{ cs_targetPipelineName }}"
                    <span class="cs-k-pop-pill">{{ cs_stagesOfPipeline(cs_targetPipelineId).length }} etapas</span>
                  </div>
                  <div v-for="s in cs_stagesOfPipeline(cs_targetPipelineId)" :key="s.id" class="cs-k-pop-item"
                       :class="{ selected: step.config?.stage_id === s.id }"
                       @click.stop="cs_setTargetStage(s.id)">
                    <span class="cs-k-pop-stage-dot" :style="{ background: s.color || '#fbbf24' }"></span>
                    <div class="cs-k-pop-text">{{ s.name }}</div>
                    <span v-if="step.config?.stage_id === s.id" class="cs-k-pop-check">✓</span>
                  </div>
                </div>

                <div v-if="cs_popOpen === 'spipeline'" v-on-click-outside="cs_closePop" class="cs-k-pop cs-k-pop-pipe">
                  <div class="cs-k-pop-head">Elige embudo fuente <span class="cs-k-pop-pill">{{ availablePipelines.length }} embudos</span></div>
                  <div v-for="p in availablePipelines" :key="p.id" class="cs-k-pop-item"
                       :class="{ selected: cs_sourcePipelineId === p.id }"
                       @click.stop="cs_setSourcePipeline(p.id)">
                    <span class="cs-k-pop-ico" style="background:rgba(96,165,250,0.18);color:#93c5fd;">⊟</span>
                    <div class="cs-k-pop-text">{{ p.name }}
                      <span class="cs-k-pop-meta">{{ (p.stages || []).length }} etapas</span>
                    </div>
                    <span v-if="cs_sourcePipelineId === p.id" class="cs-k-pop-check">✓</span>
                  </div>
                </div>

                <div v-if="cs_popOpen === 'sstage'" v-on-click-outside="cs_closePop" class="cs-k-pop cs-k-pop-stage">
                  <div class="cs-k-pop-head">Etapa fuente · "{{ cs_sourcePipelineName }}"
                    <span class="cs-k-pop-pill">{{ cs_stagesOfPipeline(cs_sourcePipelineId).length }} etapas</span>
                  </div>
                  <div v-for="s in cs_stagesOfPipeline(cs_sourcePipelineId)" :key="s.id" class="cs-k-pop-item"
                       :class="{ selected: step.config?.source_stage_id === s.id }"
                       @click.stop="cs_setSourceStage(s.id)">
                    <span class="cs-k-pop-stage-dot" :style="{ background: s.color || '#94a3b8' }"></span>
                    <div class="cs-k-pop-text">{{ s.name }}</div>
                    <span v-if="step.config?.source_stage_id === s.id" class="cs-k-pop-check">✓</span>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <template v-for="f in actionFieldsFor(step.config?.action_type)" :key="f.key">
              <!-- Selector visual de etiquetas para add_label / remove_label -->
              <ActionLabelSelector
                v-if="f.key === 'labels'"
                :model-value="step.config?.labels"
                @update:model-value="patchConfig({ labels: $event }, true)"
              />
              <select v-else-if="f.options" :value="step.config?.[f.key] || ''" class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12"
                      @change="patchConfig({ [f.key]: $event.target.value }, true)">
                <option v-for="o in f.options" :key="o" :value="o">{{ o }}</option>
              </select>
              <input v-else :value="step.config?.[f.key] || ''" :type="f.type || 'text'" :placeholder="f.label"
                     class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400"
                     @input="patchConfig({ [f.key]: f.type === 'number' ? (parseInt($event.target.value, 10) || 0) : $event.target.value })" />
            </template>
          </template>
        </template>

        <!-- REACCIÓN -->
        <template v-else-if="step.step_type === 'reaction'">
          <div class="text-[10px] text-n-slate-11">Emoji a reaccionar:</div>
          <input :value="step.config?.emoji || ''" type="text" maxlength="4" placeholder="❤"
                 class="w-20 px-2 py-1 text-center rounded border border-n-weak bg-n-background text-xl focus:outline-none focus:border-blue-400"
                 @input="patchConfig({ emoji: $event.target.value })" />
        </template>

        <!-- COMENTARIO -->
        <template v-else-if="step.step_type === 'comment'">
          <div class="text-[10px] text-n-slate-11">📝 Nota privada (sólo agentes):</div>
          <textarea :value="step.config?.content || ''" rows="3" placeholder="Lead caliente — confirmar precio…"
                    class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400 resize-none"
                    @input="patchConfig({ content: $event.target.value })" />
        </template>

        <!-- VALIDACIÓN -->
        <template v-else-if="step.step_type === 'validation'">
          <div class="text-[10px] text-n-slate-11">Esperar respuesta del cliente:</div>
          <select :value="step.config?.expect || 'any'"
                  class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12"
                  @change="patchConfig({ expect: $event.target.value }, true)">
            <option value="any">Cualquier respuesta</option>
            <option value="number">Número</option>
            <option value="email">Email válido</option>
            <option value="phone">Teléfono</option>
            <option value="regex">Regex personalizado</option>
          </select>
          <input v-if="step.config?.expect === 'regex'" :value="step.config?.pattern || ''" type="text" placeholder="^\\d{4,}$"
                 class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs font-mono text-n-slate-12 focus:outline-none focus:border-blue-400"
                 @input="patchConfig({ pattern: $event.target.value })" />
          <div class="flex items-center gap-1.5">
            <input :value="step.config?.timeout_minutes ?? 60" type="number" min="1" placeholder="60"
                   class="w-16 px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400"
                   @input="patchConfig({ timeout_minutes: parseInt($event.target.value, 10) || 60 })" />
            <span class="text-[10px] text-n-slate-11">min timeout</span>
          </div>
        </template>

        <!-- SUSCRIBIRSE META -->
        <template v-else-if="step.step_type === 'subscribe_meta'">
          <div class="text-[10px] text-n-slate-11">Suscribir a lista:</div>
          <input :value="step.config?.list || 'broadcasts'" type="text" placeholder="broadcasts"
                 class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400"
                 @input="patchConfig({ list: $event.target.value })" />
        </template>

        <!-- ROUND ROBIN -->
        <template v-else-if="step.step_type === 'round_robin'">
          <div class="text-[10px] text-n-slate-11">Equipo (vacío = admins):</div>
          <input :value="step.config?.team_id || ''" type="number" placeholder="ID del equipo"
                 class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400"
                 @input="patchConfig({ team_id: parseInt($event.target.value, 10) || null })" />
        </template>

        <!-- WIDGET -->
        <template v-else-if="step.step_type === 'widget'">
          <div class="text-[10px] text-n-slate-11">Tipo de widget:</div>
          <select :value="step.config?.widget_type || 'calendar'"
                  class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12"
                  @change="patchConfig({ widget_type: $event.target.value }, true)">
            <option value="calendar">Calendario / Booking</option>
            <option value="form">Formulario</option>
            <option value="payment">Pago</option>
            <option value="other">Otro</option>
          </select>
          <input :value="step.config?.intro || ''" type="text" placeholder="Texto introductorio"
                 class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400"
                 @input="patchConfig({ intro: $event.target.value })" />
          <input :value="step.config?.url || ''" type="text" placeholder="https://cal.com/…"
                 class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400"
                 @input="patchConfig({ url: $event.target.value })" />
        </template>

        <!-- CODE -->
        <template v-else-if="step.step_type === 'code'">
          <div class="text-[10px] text-n-slate-11">⚙ Ruby (timeout 5s):</div>
          <textarea :value="step.config?.code || ''" rows="5" placeholder="set('weekday', Time.current.strftime('%A'))"
                    class="w-full px-2 py-1.5 rounded border border-n-weak bg-n-alpha-black1 text-[10px] font-mono text-n-slate-12 focus:outline-none focus:border-blue-400 resize-none"
                    @input="patchConfig({ code: $event.target.value })" />
        </template>

        <!-- INICIAR SALESBOT (Kommo-style teal · 1 línea · click sobre nombre cambia bot) -->
        <template v-else-if="step.step_type === 'subbot'">
          <div class="flex items-center gap-2.5 px-1 py-1.5 rounded-lg bg-n-alpha-black1 border border-teal-500/30">
            <span class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style="background: linear-gradient(135deg,#14b8a6,#0d9488); box-shadow: 0 2px 4px rgba(13,148,136,.25);">
              <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/>
              </svg>
            </span>
            <span class="text-[12px] text-n-slate-11 whitespace-nowrap flex-shrink-0">Seleccione Salesbot:</span>
            <div class="relative flex-1 min-w-0">
              <span class="text-[13px] font-semibold truncate block"
                    :class="step.config?.bot_id ? 'text-n-slate-12' : 'text-teal-600 italic'"
                    :title="selectedBotName"
                    style="text-decoration: underline; text-decoration-color: #14b8a6; text-decoration-thickness: 1.5px; text-underline-offset: 3px; cursor: pointer;">
                {{ selectedBotName || '— elegir bot —' }}
              </span>
              <select :value="step.config?.bot_id || ''"
                      class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      @change="patchConfig({ bot_id: parseInt($event.target.value, 10) || null }, true)">
                <option value="">— elegir bot —</option>
                <option v-for="b in availableBots" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
            </div>
            <button v-if="step.config?.bot_id"
                    class="w-[18px] h-[18px] rounded-full bg-slate-300 hover:bg-rose-400 text-white text-[11px] font-bold leading-none flex items-center justify-center flex-shrink-0"
                    title="Quitar bot seleccionado"
                    @click.stop="patchConfig({ bot_id: null }, true)">×</button>
          </div>
          <p v-if="!availableBots.length" class="text-[10px] text-amber-600 mt-1 px-1">No hay otros bots disponibles</p>
          <p class="text-[10px] text-n-slate-10 mt-2 px-1 leading-snug">
            ⏸️ El flujo principal se detiene aquí y solo continúa cuando este sub-bot termina todos sus pasos.
          </p>
        </template>

        <!-- LIST MESSAGE (header/body inline, sections via panel) -->
        <template v-else-if="step.step_type === 'list_message'">
          <input :value="step.config?.header || ''" type="text" placeholder="Encabezado"
                 class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400"
                 @input="patchConfig({ header: $event.target.value })" />
          <textarea :value="step.config?.body || ''" rows="2" placeholder="Cuerpo del mensaje"
                    class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400 resize-none"
                    @input="patchConfig({ body: $event.target.value })" />
          <input :value="step.config?.footer || ''" type="text" placeholder="Pie"
                 class="w-full px-2 py-1 rounded border border-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-none focus:border-blue-400"
                 @input="patchConfig({ footer: $event.target.value })" />
          <div class="text-[10px] text-n-slate-11">{{ (step.config?.sections || []).length }} secciones</div>
        </template>

        <!-- IR A (goto · convergencia multi-entrada) -->
        <template v-else-if="step.step_type === 'goto'">
          <div class="flex items-center gap-2 px-2 py-1.5 text-[12px] text-indigo-600 dark:text-indigo-300">
            <span class="text-[14px]">↪</span>
            <span>Continúa en el paso <b>#{{ step.config?.target_step_id ?? '—' }}</b></span>
          </div>
        </template>

        <!-- Fallback -->
        <div v-else class="text-xs text-n-slate-12 px-1">{{ previewText }}</div>
      </div>

      <div v-if="step.step_type === 'condition' || step.step_type === 'subbot' || step.step_type === 'pause'">
        <!-- condition: Else inline · subbot/pause: el + del header reemplaza al footer + el conn-dot embebido -->
      </div>
      <div v-else class="border-t border-n-weak">
        <button class="w-full py-1.5 text-[10px] hover:bg-n-alpha-black1 text-blue-600 font-medium" @click.stop="emit('add-child', step, $event)">
          + Agregar siguiente paso
        </button>
      </div>
    </div>
  </div>
</template>

<style>
/* V5-P2 · 2026-05-15 (Victor) · Cambiar etapa estilo Kommo · 2 secciones (Ejecutar + Acción) · M1.B sutil */
.cs-k-card {
  background: linear-gradient(135deg, rgba(28,28,28,0.85), rgba(18,18,18,0.85));
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  overflow: visible;
  position: relative;
  margin-top: 4px;
}
.cs-k-section {
  padding: 11px 14px;
  display: flex; align-items: center; gap: 7px; flex-wrap: wrap;
  position: relative;
}
.cs-k-section + .cs-k-section {
  border-top: 1px dashed rgba(255,255,255,0.06);
}
.cs-k-section-label {
  font-size: 9.5px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase;
  color: rgba(255,255,255,0.50); width: 72px; flex-shrink: 0;
  display: flex; align-items: center; gap: 4px;
}
.cs-k-section-icon {
  width: 13px; height: 13px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 10px; color: rgba(251,191,36,0.65);
}
.cs-k-section-helper {
  margin-left: auto; font-size: 10px; color: rgba(255,255,255,0.40); font-style: italic;
}

.cs-k-word { color: rgba(255,255,255,0.50); font-size: 10.5px; font-weight: 500; }
.cs-k-arrow { color: rgba(251,191,36,0.55); font-size: 12px; font-weight: 800; padding: 0 1px; }

.cs-k-toggle {
  display: inline-flex; background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 5px; padding: 2px;
}
.cs-k-toggle-btn {
  background: transparent; border: 0; color: rgba(255,255,255,0.55);
  font-size: 9.5px; font-weight: 700; padding: 3px 7px; border-radius: 3px;
  cursor: pointer; display: inline-flex; align-items: center; gap: 3px;
  transition: all 0.15s;
}
.cs-k-toggle-btn.active {
  background: rgba(251,191,36,0.15); color: #fbbf24;
}

.cs-k-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  font-size: 10.5px; font-weight: 600; color: #fff;
  cursor: pointer; transition: all 0.15s;
  white-space: nowrap;
}
.cs-k-chip:hover { border-color: rgba(251,191,36,0.40); background: rgba(251,191,36,0.06); }
.cs-k-chip.cs-k-chip-to {
  background: rgba(251,191,36,0.10); border-color: rgba(251,191,36,0.35); color: #fde68a;
}
.cs-k-chip.cs-k-chip-from {
  background: rgba(148,163,184,0.10); border-color: rgba(148,163,184,0.30); color: #cbd5e1;
}
.cs-k-chip.cs-k-chip-trigger {
  background: rgba(96,165,250,0.10); border-color: rgba(96,165,250,0.35); color: #93c5fd;
}
.cs-k-chip.cs-k-chip-trigger:hover {
  border-color: rgba(96,165,250,0.55); background: rgba(96,165,250,0.15);
}
.cs-k-chip.placeholder { font-style: italic; color: rgba(255,255,255,0.45); font-weight: 500; }
.cs-k-chip.disabled { opacity: 0.50; cursor: not-allowed; }
.cs-k-chip-dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
.cs-k-chip-ico { font-size: 10.5px; opacity: 0.85; }
.cs-k-chip-caret { font-size: 7.5px; opacity: 0.65; margin-left: 1px; }

/* === Popovers · estilo dropdown rich (replace native selects) === */
.cs-k-pop {
  position: absolute;
  background: linear-gradient(135deg, rgba(20,20,20,0.98), rgba(8,8,8,0.98));
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 9px; padding: 5px;
  min-width: 240px; max-width: 380px;
  box-shadow: 0 16px 40px rgba(0,0,0,0.60), 0 0 0 1px rgba(251,191,36,0.10);
  z-index: 100;
  max-height: 320px; overflow-y: auto;
}
.cs-k-pop-trigger { top: 100%; left: 12px; right: auto; margin-top: 4px; min-width: 320px; }
.cs-k-pop-pipe   { top: calc(100% + 4px); left: 12px; min-width: 260px; }
.cs-k-pop-stage  { top: calc(100% + 4px); left: 12px; min-width: 240px; }

.cs-k-pop-head {
  padding: 7px 9px;
  font-size: 9.5px; font-weight: 800; letter-spacing: 0.7px; text-transform: uppercase;
  color: rgba(255,255,255,0.55);
  display: flex; align-items: center; gap: 5px;
}
.cs-k-pop-pill {
  margin-left: auto; font-size: 8.5px; padding: 2px 6px; border-radius: 8px;
  background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.55);
  text-transform: none; letter-spacing: 0; font-weight: 600;
}
.cs-k-pop-section {
  padding: 5px 9px 3px;
  font-size: 8.5px; font-weight: 800; letter-spacing: 0.7px; text-transform: uppercase;
  color: rgba(255,255,255,0.40);
}
.cs-k-pop-item {
  padding: 6px 8px; border-radius: 5px;
  display: flex; align-items: center; gap: 7px;
  cursor: pointer; transition: background 0.12s;
  font-size: 11px; color: rgba(255,255,255,0.85);
}
.cs-k-pop-item:hover { background: rgba(251,191,36,0.08); }
.cs-k-pop-item.selected { background: rgba(251,191,36,0.14); color: #fff; font-weight: 600; }
.cs-k-pop-ico {
  width: 18px; height: 18px; border-radius: 4px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 10px; flex-shrink: 0;
  background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.65);
}
.cs-k-pop-stage-dot {
  width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; margin-left: 2px;
  border: 1px solid rgba(255,255,255,0.15);
}
.cs-k-pop-text { flex: 1; font-size: 11px; line-height: 1.3; }
.cs-k-pop-meta {
  display: block; font-size: 9px; color: rgba(255,255,255,0.40); margin-top: 1px; font-weight: 500;
}
.cs-k-pop-check { color: #fbbf24; font-weight: 800; font-size: 11px; }
.cs-k-pop-divider { height: 1px; background: rgba(255,255,255,0.06); margin: 3px 6px; }
.cs-k-pop-delay {
  display: flex; align-items: center; gap: 4px;
  padding: 5px 9px 7px 32px;
  background: rgba(251,191,36,0.05);
  font-size: 10px; color: rgba(255,255,255,0.65);
  border-bottom: 1px solid rgba(255,255,255,0.04);
}
.cs-k-pop-delay-label { font-weight: 700; color: rgba(251,191,36,0.85); margin-right: 2px; }
.cs-k-pop-delay input {
  width: 38px; background: rgba(0,0,0,0.40);
  border: 1px solid rgba(255,255,255,0.10);
  color: #fff; font-size: 10.5px; font-weight: 600;
  padding: 2px 4px; border-radius: 4px; outline: none; text-align: center;
}
.cs-k-pop-delay input:focus { border-color: rgba(251,191,36,0.50); }
</style>
